import type { Messages } from './fr';

/**
 * English UI copy, written alongside the French source (`fr.ts`). Typed as
 * `Messages`, so a key missing here fails the type check. Placeholders start
 * with "e.g. ", the English counterpart of the "ex. " rule.
 */
export const en: Messages = {
  common: {
    optional: 'optional',
    loading: 'Loading…',
    close: 'Close',
    dismiss: 'Dismiss',
    back: 'Back',
    removeTag: 'Remove tag {{name}}',
    closeDialog: 'Close dialog',
    loadingList: 'Loading',
    dismissNotification: 'Dismiss notification',
    uploadTooLarge: 'This file is too large to upload.',
    uploadFailed: 'The upload failed.',
    uploadOffline: 'The upload failed. Check your connection and try again.',
  },
  errorPage: {
    title: 'Something went wrong on this page',
    body: 'Nothing you saved is lost. Reloading usually fixes it; if it keeps happening, tell us what you were doing.',
    reload: 'Reload the page',
  },
  status: {
    draft: 'Draft',
    active: 'Active',
    paused: 'Paused',
    scheduled: 'Scheduled',
    published: 'Published',
    failed: 'Failed',
    cancelled: 'Cancelled',
    stopped: 'Stopped',
    completed: 'Completed',
    noTime: 'No time yet',
    unpublishedChanges: 'Unpublished changes',
    atLimit: 'Limit reached',
    connected: 'Connected',
    expired: 'Expired',
    error: 'Error',
    disconnected: 'Disconnected',
  },
  channel: {
    instagram: 'Instagram',
    messenger: 'Messenger',
    facebook: 'Facebook',
  },
  /** "Saved · just now" beside an autosaved form (the builder, the composer, inbox settings). */
  saveState: {
    saved: 'Saved',
    saving: 'Saving…',
    savingAnnounce: 'Saving',
    failed: 'Couldn’t save — {{reason}}',
    failedAnnounce: 'Couldn’t save: {{reason}}',
    connectionDropped: 'The connection dropped.',
    retry: 'Retry',
  },
  posts: {
    kind: {
      post: {
        title: 'Posts',
        lede: 'Schedule photos, carousels and Reels to publish on Instagram and Facebook automatically.',
        newLabel: 'New post',
        tabsLabel: 'Post status',
        tableLabel: 'Posts',
        noun: 'Post',
        cancelTitle: 'Cancel this post?',
        cancelAction: 'Cancel post',
        deleteTitle: 'Delete this post?',
        deleteAction: 'Delete post',
        deletePublished: 'This removes it from Repliq only. The post stays on {{where}}.',
        canceled: 'Post cancelled',
        retried: 'Post queued to publish now',
        deleted: 'Post deleted',
        insights: 'Post insights',
        empty: {
          upcoming: {
            title: 'Nothing scheduled',
            body: 'Posts you schedule appear here in date order. Use New post to plan the next one.',
          },
          drafts: {
            title: 'No drafts',
            body: 'Start a post and leave — once it has a photo or video, it’s saved here automatically.',
          },
          published: {
            title: 'Nothing published yet',
            body: 'Published posts show views, reach and engagement here.',
          },
        },
      },
      story: {
        title: 'Stories',
        lede: 'Schedule stories to go up on Instagram and Facebook automatically — one photo or video each, up for 24 hours.',
        newLabel: 'New story',
        tabsLabel: 'Story status',
        tableLabel: 'Stories',
        noun: 'Story',
        cancelTitle: 'Cancel this story?',
        cancelAction: 'Cancel story',
        deleteTitle: 'Delete this story?',
        deleteAction: 'Delete story',
        deletePublished: 'This removes it from Repliq only. The story stays on {{where}} for the rest of its day.',
        canceled: 'Story cancelled',
        retried: 'Story queued to publish now',
        deleted: 'Story deleted',
        insights: 'Story insights',
        empty: {
          upcoming: {
            title: 'No story scheduled',
            body: 'Stories you schedule appear here in date order. Use New story to plan the next one.',
          },
          drafts: {
            title: 'No drafts',
            body: 'Start a story and leave — once it has a photo or video, it’s saved here automatically.',
          },
          published: {
            title: 'Nothing published yet',
            body: 'Published stories show their views, reach and replies here, gathered over their 24 hours.',
          },
        },
      },
    },
    columns: {
      status: 'Status',
      goesLive: 'Goes live',
      edited: 'Last edited',
      reason: 'Reason',
      actions: 'Actions',
      views: 'Views',
      reach: 'Reach',
      likes: 'Likes',
      comments: 'Comments',
      saved: 'Saves',
      replies: 'Replies',
      shares: 'Shares',
    },
    legend: {
      post: '“—”: no data for this post · “0”: no interaction',
      story: '“—”: no stats yet · “0”: no interaction',
    },
    narrow: {
      views_one: '{{value}} view',
      views_other: '{{value}} views',
      likes_one: '{{value}} like',
      likes_other: '{{value}} likes',
      replies_one: '{{value}} reply',
      replies_other: '{{value}} replies',
    },
    noCaption: 'No caption',
    storyFallback: 'Story',
    items_one: '{{count}} item',
    items_other: '{{count}} items',
    rowActions: 'Actions for “{{name}}”',
    publishing: 'Publishing…',
    alsoOn: 'Also on {{name}}',
    campaignChip: 'Campaign · {{name}}',
    automationAnswers: '{{name}} answers comments on this post',
    automationOff: '{{name}} is off',
    off: '(off)',
    onPage: 'On {{page}}: {{error}}',
    retry: 'Retry now',
    synced: 'Insights synced {{ago}}',
    menu: {
      edit: 'Edit',
      view: 'Open',
      cancel: 'Cancel',
      retry: 'Retry now',
      insights: 'Insights',
      platformInsights: '{{platform}} insights',
      viewOn: 'View on {{platform}}',
      delete: 'Delete',
    },
    /** The slide's words when the menu's are too long for it (D-196). */
    swipe: { retry: 'Retry' },
    keep: 'Keep it',
    pausedAutomations_one: '{{count}} automation paused: {{names}}.',
    pausedAutomations_other: '{{count}} automations paused: {{names}}.',
    actionFailed: 'That did not work',
    deleteUnpublished: 'It will not be published. The uploaded media stays in your library.',
    deleteCopy: 'Its copy on {{page}} is deleted with it.',
    deleteAutomationsOne: 'The automation {{names}} is pinned to it and will be paused.',
    deleteAutomationsMany: 'The {{count}} automations {{names}} are pinned to it and will be paused.',
    tabs: {
      upcoming: 'Upcoming',
      drafts: 'Drafts',
      published: 'Published',
      failed: 'Failed & cancelled',
    },
    failedCount: '{{count}} failed',
    cancelledNote: 'Cancelled: it will not be published.',
    storyTitle: 'Story on {{date}}',
    plannedFor: 'planned for {{when}}',
    edited: 'Edited {{when}}',
    noFailures: {
      post: {
        title: 'No failed posts',
        body: 'If Instagram or Facebook rejects a post, it lands here with the reason and a one-click retry.',
      },
      story: {
        title: 'No failed stories',
        body: 'If Instagram or Facebook rejects a story, it lands here with the reason and a one-click retry.',
      },
    },
    cancelConfirm: {
      intro: {
        post: 'It will not be published.',
        story: 'It will not be published.',
      },
      waitsOne: {
        post: 'The automation {{names}} waits for it and stays set up: it goes live if you schedule the post again.',
        story: 'The automation {{names}} waits for it and stays set up: it goes live if you schedule the story again.',
      },
      waitsMany: {
        post: 'The {{count}} automations {{names}} wait for it and stay set up: they go live if you schedule the post again.',
        story: 'The {{count}} automations {{names}} wait for it and stay set up: they go live if you schedule the story again.',
      },
    },
  },
  calendar: {
    /** The header when the calendar shows one campaign (`?campaign=`, D-190). */
    campaignPage: {
      eyebrow: 'Campaign calendar',
      back: 'Back to the campaign',
      lede: 'This campaign\x27s posts and stories. Clear the campaign filter to see the whole calendar.',
    },
    viewLabel: 'View',
    views: {
      list: 'List',
      calendar: 'Calendar',
    },
    viewIn: 'View in the calendar',
    lede: 'Instagram and Facebook posts and stories, in one place.',
    posts_one: '{{count}} post',
    posts_other: '{{count}} posts',
    stories_one: '{{count}} story',
    stories_other: '{{count}} stories',
    kinds: '{{posts}}, {{stories}}',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    today: 'Today',
    filters: 'Filters',
    campaign: 'Campaign',
    allCampaigns: 'All campaigns',
    legend: 'Legend',
    day: '{{date}}, {{items}}',
    moreOneKind: '+{{kinds}}',
    moreTwoKinds: '+{{count}} · {{kinds}}',
    item: '{{what}}, {{date}} at {{time}}, {{where}}',
    what: {
      post: {
        scheduled: 'Scheduled post',
        published: 'Published post',
        failed: 'Failed post',
        draft: 'Draft post',
      },
      story: {
        scheduled: 'Scheduled story',
        published: 'Published story',
        failed: 'Failed story',
        draft: 'Draft story',
      },
    },
    page: 'Page {{name}}',
    emptyMonth: {
      title: 'Nothing scheduled in {{month}}.',
      next: 'Next to go live: {{date}} · {{time}}.',
      untimed_one: '{{count}} item is waiting for a time.',
      untimed_other: '{{count}} items are waiting for a time.',
      goTo: 'Go to {{date}}',
    },
    zone: 'Times shown in the workspace time zone: {{zone}}.',
    period: 'Period',
    periods: {
      month: 'Month',
      week: 'Week',
    },
    previousWeek: 'Previous week',
    nextWeek: 'Next week',
    thisWeek: 'This week',
    weekName: 'Week from {{first}} to {{last}}',
    weekZone: 'Times shown in the workspace time zone: {{zone}}. Click an item to open it.',
    upcoming: 'Next to go live',
    openDraft: 'Open the draft',
    untimedFooter: 'Open an item to give it a date. A campaign launches only once all its posts have a date.',
    edited: { ago: 'edited {{when}}', today: 'edited at {{when}}', date: 'edited on {{when}}' },
    all: 'All',
    untimedCount_one: '{{count}} item with no time yet',
    untimedCount_other: '{{count}} items with no time yet',
    inCampaign: '{{count}} in {{name}}',
    drafts_one: '{{count}} draft',
    drafts_other: '{{count}} drafts',
    recent: 'Recently published',
    publishedMany: 'Published',
    agendaZone: 'Times in the workspace time zone: {{zone}}.',
  },
  postType: {
    image: 'Image',
    carousel: 'Carousel',
    reel: 'Reel',
    story: 'Story',
  },
  ago: {
    now: 'just now',
    minutes: '{{count}} min ago',
    hours: '{{count}} h ago',
    yesterday: 'yesterday',
    days_one: '{{count}} day ago',
    days_other: '{{count}} days ago',
  },
  insights: {
    loading: 'Loading insights',
    pending: 'Numbers from Instagram arrive within 48 hours.',
    campaignAcross_one: 'Across {{count}} published post. Reach is added up per post, so one person who saw two posts counts twice.',
    campaignAcross_other: 'Across {{count}} published posts. Reach is added up per post, so one person who saw two posts counts twice.',
    reconnect: 'Reconnect',
    avgWatchTime: 'Avg watch time',
    automationsTitle: 'Automations on this post',
    commentsReceived: 'Comments received',
    triggered: 'Started an automation',
    messagesSent: 'DMs sent',
    contactsReached: 'People reached',
    dayByDay: 'Day by day',
    day: 'Day',
    syncedAgo: 'Synced {{ago}}',
    notSynced: 'Not synced yet',
    close: 'Close',
    refresh: 'Refresh',
    refreshed: 'Numbers refreshed',
    partlyRefreshed: 'Numbers partly refreshed',
    refreshFailed: 'Could not refresh',
    seconds: '{{value}} s',
    errors: {
      INSIGHTS_SCOPE_MISSING: 'Reconnect {{account}} in Settings → Channels to see views, reach, saves and shares.',
      ACCOUNT_NOT_CONNECTED: '{{account}} is not connected. Reconnect it in Settings → Channels.',
      INSIGHTS_UNAVAILABLE: 'Insights are not available for this channel on this server.',
      MEDIA_GONE: 'This post is no longer on {{platform}}.',
      SYNC_FAILED: '{{platform}} did not return the numbers for this post. It will be retried.',
    },
  },
  nav: {
    dashboard: 'Dashboard',
    home: 'Home',
    inbox: 'Conversations',
    contacts: 'Contacts',
    automations: 'Automations',
    templates: 'Templates',
    posts: 'Posts',
    stories: 'Stories',
    campaigns: 'Campaigns',
    analytics: 'Analytics',
    channels: 'Channels',
    settings: 'Settings',
    team: 'Team',
    inboxSettings: 'Conversations',
    tags: 'Tags',
    fields: 'Custom fields',
    usage: 'My plan',
    developers: 'Developers',
    groupAudience: 'Audience',
    groupAutomate: 'Automate',
    groupPublish: 'Publish',
    groupInsights: 'Insights',
    more: 'More',
    main: 'Main navigation',
    settingsSections: 'Settings',
    search: 'Search',
    unread_one: 'unread',
    unread_other: 'unread',
    invoice: 'Invoice',
    invoiceToPay: 'Invoice to pay',
    moreWithInvoice: 'More, invoice to pay',
  },
  shell: {
    workspaceButton: 'Workspace: {{name}}. Switch workspace',
    workspaceLoading: 'loading',
    workspaces: 'Workspaces',
    role: { owner: 'Owner', admin: 'Admin', agent: 'Agent' },
    switchFailed: 'Couldn’t switch workspace. Check your connection and try again.',
    createWorkspace: 'Create workspace',
    accountMenu: 'Account menu',
    account: 'Account',
    signOut: 'Sign out',
    signingOut: 'Signing out…',
    signOutFailed: 'Couldn’t sign out. Check your connection and try again.',
  },
  palette: {
    title: 'Search',
    inputLabel: 'Search pages, automations and contacts',
    prompt: 'Search pages, automations, contacts…',
    results: 'Results',
    sectionPages: 'Pages',
    sectionAutomations: 'Automations',
    sectionContacts: 'Contacts',
    searchingContacts: 'Searching contacts…',
    noResults: 'No results for “{{query}}”.',
    resultCount_one: '{{count}} result',
    resultCount_other: '{{count}} results',
    enter: 'Enter',
    esc: 'Esc',
    typeToSearch: 'Type to search automations and contacts too',
    toClose: 'to close',
  },
  inbox: {
    title: 'Conversations',
    open_one: '{{count}} open',
    open_other: '{{count}} open',
    unread_one: '{{count}} unread',
    unread_other: '{{count}} unread',
    filters: 'Filters',
    filtersUnreadOnly: 'Filters (unread only)',
    unreadOnly: 'Unread only',
    awaitingOnly: 'Waiting for a reply',
    filtersAwaitingOnly: 'Filters (waiting for a reply)',
    filtersBoth: 'Filters (unread, waiting for a reply)',
    awaiting: 'Waiting for a reply · {{duration}}',
    searchLabel: 'Search conversations',
    searchPrompt: 'Name, email or message',
    statusLabel: 'Conversation status',
    statusOpen: 'Open',
    statusClosed: 'Resolved',
    statusAll: 'All',
    assigneeLabel: 'Assigned to',
    assigneeAnyone: 'Anyone',
    assigneeMine: 'Mine',
    assigneeUnassigned: 'Unassigned',
    listLoading: 'Loading conversations',
    noMatch: 'Nothing matches these filters.',
    noClosed: 'No resolved conversations.',
    noOpen: 'No open conversations. You’re all caught up.',
    noMessagesYet: 'No messages yet',
    handledBy: 'Last auto reply: {{name}}',
    automationsPaused: 'Automations paused',
    closed: 'Resolved',
    pickTitle: 'Pick a conversation',
    pickBody: 'Read the history and reply. Those an automation answered last are marked',
    pickMarker: 'Last auto reply',
    neverTitle: 'No conversations yet',
    neverBody: 'Connect Instagram or Messenger and every DM lands here — answered by your automations or your team.',
    connectChannel: 'Connect a channel',
    setUpReplies: 'Set up auto-replies',
    unknownContact: 'Unknown contact',
    mockChannel: 'Mock channel',
  },
  thread: {
    label: 'Conversation with {{name}}',
    messages: 'Messages',
    back: 'Back to conversations',
    loading: 'Loading conversation',
    gone: 'This conversation no longer exists.',
    assignLabel: 'Assigned to',
    unassigned: 'Unassigned',
    /** The phone's icon-only assign control says who has the conversation (A16-02). */
    assignedToName: 'Assigned to {{name}}',
    assignNobody: 'Unassigned: assign the conversation',
    resolve: 'Resolve',
    reopen: 'Reopen',
    resolveLabel: 'Resolve conversation',
    reopenLabel: 'Reopen conversation',
    loadEarlier: 'Load earlier messages',
    loadingMessages: 'Loading messages',
    noMessages: 'No messages in this conversation yet.',
    noText: '(no text)',
    system: 'System',
    automatedReply: 'Automated reply',
    queued: 'Queued',
    failed: 'Failed to send',
    timesTwo: '{{first}} and {{last}}',
    timesRange: '{{first}} to {{last}}',
    today: 'Today',
    yesterday: 'Yesterday',
    resumed: 'Automations resumed',
    resumeFailed: 'Could not resume automations',
    pauseFailed: 'Could not pause automations',
    replyFailed: 'Reply not sent',
    assignFailed: 'Could not assign the conversation',
    resolvedToast: 'Conversation resolved',
    reopenedToast: 'Conversation reopened',
    updateFailed: 'Could not update the conversation',
  },
  replyBox: {
    reply: 'Reply',
    replyPrompt: 'Reply to {{name}}…',
    send: 'Send',
    sending: 'Sending…',
    hintPause: 'Your reply pauses automations for this contact for {{duration}}.',
    hintPauseUnknown: 'Your reply pauses automations for this contact.',
    hintNoPause: 'Your replies don’t pause automations.',
    hintReopen: 'Replying reopens the conversation.',
    windowClosed: 'You can’t reply: {{channel}} only accepts a reply within 24 hours of the contact’s last message, which came {{since}} ago. You can reply as soon as they write again.',
    windowNever: 'You can’t reply: {{channel}} only accepts a reply after the contact writes. You can reply as soon as they do.',
    windowClosing: '{{left}} left to reply: after that, {{channel}} accepts no reply until the contact writes again.',
  },
  handover: {
    none: 'No automation is running for this contact.',
    running: 'Running for this contact: {{names}}.',
    waitsUntil: '{{name}} (waits until {{time}})',
    ifReply: 'If you reply, they pause for',
    ifReplyUnknown: 'If you reply, they pause.',
    noPauseOnReply: 'Your replies don’t pause them.',
    pause: 'Pause automations',
    pausedShort: 'Paused until {{time}}',
    runningShort: 'Running: {{names}}',
    noneShort: 'No automation running',
    pausedUntil: 'Automations are paused for this contact until {{time}}.',
    pausedNote: 'Other contacts are not affected. Resolving the conversation also lifts the pause.',
    resume: 'Resume',
  },
  duration: {
    minutes: '{{count}} min',
    hours_one: '{{count}} hour',
    hours_other: '{{count}} hours',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    week: '1 week',
  },
  /** Time left before a planned instant (`formatCountdown`, Murmura M1): "in 10 h 16 min". */
  countdown: {
    in: 'in',
    text: 'in {{span}}',
    hours: '{{count}} h',
    hoursMinutes: '{{hours}} h {{minutes}} min',
  },
  contactPanel: {
    label: 'Contact details',
    openContact: 'Open contact',
    tags: 'Tags',
    details: 'Details',
    addTag: 'Add tag',
    noTagsYet: 'No tags yet — create one in Settings',
    allTagsOn: 'Every tag is already on this contact.',
    loadingTags: 'Loading tags…',
    addTagFailed: 'Could not add the tag',
    email: 'Email',
    phone: 'Phone',
    firstSeen: 'First seen',
    sourceChannel: 'Source',
    source: 'Source',
    sourceManual: 'Added manually',
    sourceImport: 'CSV import',
    sourceApi: 'Added via API',
    sourceAutomation: 'Created by an automation',
    yes: 'Yes',
    no: 'No',
    nothingOnFile: 'Nothing on file yet.',
  },
  workspace: {
    namePlaceholder: 'e.g. Acme Marketing',
    nameNeeded: 'Type a name to continue.',
    steps: 'Getting started',
    step: {
      name: '1 · Name the workspace',
      channel: '2 · Connect a channel',
      automation: '3 · First automation',
    },
    stepCurrent: 'current step',
    title: 'Name your workspace',
    lede: 'A workspace holds your contacts, automations and connected channels. You can create more later.',
    name: 'Workspace name',
    nameHint: 'Usually your company or brand name.',
    create: 'Create workspace',
    creating: 'Creating…',
  },
  auth: {
    rail: {
      tagline: 'Conversations that keep working after you log off.',
      body: 'Build your automations once: they run on Instagram and Messenger, where your customers already write to you.',
      footer: 'Multi-channel marketing automation',
    },
    genericError: 'Something went wrong. Please try again.',
    email: 'Email',
    password: 'Password',
    passwordRule: 'At least 10 characters.',
    backToLogin: 'Back to sign in',
    captcha: {
      pending: 'Security check in progress…',
      required: 'Complete the security check to continue.',
      failed: 'The security check was refused. Please try again.',
      unavailable: 'The security check is unavailable right now. Please try again in a moment.',
    },
    login: {
      title: 'Welcome back',
      lede: 'Sign in to your workspace.',
      forgot: 'Forgot password?',
      submit: 'Sign in',
      submitting: 'Signing in…',
      noAccount: 'No account?',
      register: 'Create one',
    },
    register: {
      title: 'Create your account',
      lede: 'Your first automation in a few minutes.',
      firstName: 'First name',
      lastName: 'Last name',
      submit: 'Create account',
      submitting: 'Creating account…',
      already: 'Already registered?',
      login: 'Sign in',
      sentTitle: 'Check your email',
      sentLede: 'We sent a verification link to {{email}}.',
      sentBody: 'Open the link to activate your account. In local development the message is waiting in Mailpit at',
    },
    forgot: {
      title: 'Reset your password',
      lede: 'We will email you a link.',
      submit: 'Send reset link',
      submitting: 'Sending…',
      sent: 'If an account exists for {{email}}, a reset link is on its way. The link expires in an hour.',
    },
    reset: {
      title: 'Choose a new password',
      lede: 'This signs you out everywhere else.',
      newPassword: 'New password',
      submit: 'Update password',
      submitting: 'Updating…',
      done: 'Password updated.',
      login: 'Sign in',
      missingTitle: 'This reset link is incomplete.',
      missingBody: 'Its security token is missing: request a new link.',
      requestNew: 'Request a new link',
    },
    verify: {
      title: 'Verify your email',
      lede: 'One click and your account is active.',
      submit: 'Verify my email',
      submitting: 'Verifying…',
      done: 'Your email address is verified.',
      continue: 'Continue to Repliq',
      missingTitle: 'This verification link is incomplete.',
      missingBody: 'Request a new verification email.',
      requestNew: 'Request a new email',
      requesting: 'Sending…',
      resent: 'If an unverified account exists for {{email}}, a new verification link is on its way.',
    },
    invite: {
      title: 'Join the workspace',
      ledeSignIn: 'Sign in with the invited address to accept.',
      ledeChecking: 'Checking your session…',
      ledeAccepting: 'Accepting as {{email}}.',
      signIn: 'Sign in to continue',
      accept: 'Accept invitation',
      accepting: 'Joining…',
      missing: 'This invitation link is incomplete: ask the person who invited you to send it again.',
      /** The INVITING workspace is full (D-183): its name and its owner's, never "your plan". */
      full_one: '{{workspaceName}} has reached its limit of {{limit}} user. Ask {{ownerName}} to free up a seat or upgrade the plan.',
      full_other: '{{workspaceName}} has reached its limit of {{limit}} users. Ask {{ownerName}} to free up a seat or upgrade the plan.',
      /** `ownerName` when the inviting workspace has no active owner. */
      theOwner: 'its owner',
    },
  },
  campaigns: {
    namePlaceholder: 'e.g. Autumn collection launch',
    nameNeeded: 'Give it a name to create the campaign.',
    launchNeedsPosts: 'Add at least one post to launch.',
    title: 'Campaigns',
    lede: 'Plan a series of posts, check them together, and launch them in one go.',
    new: 'New campaign',
    empty: {
      title: 'No campaigns yet',
      body: 'A campaign is a list of posts with planned times. Nothing publishes until you launch it.',
      action: 'Create a campaign',
    },
    meta: {
      posts_one: '{{count}} post',
      posts_other: '{{count}} posts',
      stories_one: '{{count}} story',
      stories_other: '{{count}} stories',
      accounts_one: '{{count}} account',
      accounts_other: '{{count}} accounts',
      noDates: 'no times yet',
      none: 'No posts yet',
      draftNote: 'nothing goes out until you launch the campaign',
    },
    row: {
      untimed_one: '{{count}} post without a time before launch',
      untimed_other: '{{count}} posts without a time before launch',
      addPost: 'Add a post',
      addPostTo: 'Add a post to {{name}}',
      progressLabel: 'Published',
      progress: '{{published}}/{{total}} published',
      failed_one: '{{count}} failed',
      failed_other: '{{count}} failed',
      /** The row's `···` and the tray a finger slides out (D-191). */
      actions: 'Actions for {{name}}',
      swipe: { pause: 'Pause', resume: 'Resume', delete: 'Delete' },
    },
    create: {
      title: 'New campaign',
      name: 'Name',
      description: 'Description',
      descriptionNote: 'optional, for your team',
      cancel: 'Cancel',
      submit: 'Create campaign',
    },
    detail: {
      back: 'Campaigns',
      actions: 'Campaign actions',
      add: 'Add',
      addMenu: 'Add to the campaign',
      addPost: 'Post',
      addStory: 'Story',
      launch: 'Launch campaign',
      resume: 'Resume campaign',
      rename: 'Rename',
      pause: 'Pause campaign',
      cancelCampaign: 'Stop campaign',
      delete: 'Delete campaign',
      toast: { launch: 'Campaign launched', resume: 'Campaign resumed', pause: 'Campaign paused', cancel: 'Campaign stopped', deleted: 'Campaign deleted' },
      check: 'Check before launching:',
      untimed_one: '{{count}} post has no time yet.',
      untimed_other: '{{count}} posts have no time yet.',
      /** Under the disabled Launch (A11-01). */
      launchReason_one: '{{count}} post has no time: add a date to launch.',
      launchReason_other: '{{count}} posts have no time: add a date to launch.',
      launchFailed: 'The campaign was not launched. Fix these first:',
      launchProblems: 'Launch problems',
      kpi: {
        planned: 'Planned',
        timed: 'With a time',
        published: 'Published',
        of: 'of {{total}}',
        next: 'Next post: {{when}}',
        reachLater: 'Reach shows after launch',
        reach: 'Reach: {{value}}',
      },
      notes: {
        paused: 'Paused: planned times are kept, and a post already being published finishes. Resume to schedule the posts again.',
        active: 'Live since {{when}}.',
        completed: 'Every post has been published.',
        completedFailed_one: 'Finished with {{count}} failed post. Edit it to plan it again.',
        completedFailed_other: 'Finished with {{count}} failed posts. Edit them to plan them again.',
        canceled: 'Stopped: posts that had not been published will not be.',
      },
      timeline: 'Timeline',
      setTime: 'Set the time',
      actionFor: '{{action}}: {{title}}',
      view: 'View',
      copyOf: 'copy of the {{platform}} post',
      publishing: 'Publishing',
      viewOn: 'View “{{title}}” on {{platform}}',
      viewOnShort: 'View on {{platform}}',
      empty: {
        title: 'Nothing in this campaign yet',
        body: 'Add posts and stories with the time each one should go out. They are checked together when you launch.',
        firstPost: 'Add the first post',
        firstStory: 'Add the first story',
      },
      numbers: 'Published numbers',
      confirm: {
        cancelTitle: 'Stop this campaign?',
        deleteTitle: 'Delete this campaign?',
        /** From the list, where the page does not say which (D-191). */
        cancelTitleNamed: 'Stop “{{name}}”?',
        deleteTitleNamed: 'Delete “{{name}}”?',
        cancelBody: 'Posts that have not been published will be canceled. This cannot be undone.',
        deleteBody: 'The campaign and its unpublished posts are deleted. Published posts stay where they were published, and in Posts.',
        keep: 'Keep it',
      },
      edit: {
        title: 'Rename campaign',
        name: 'Name',
        description: 'Description',
        cancel: 'Cancel',
        save: 'Save',
        nameNeeded: 'Enter a name to save.',
      },
    },
  },
  newAutomation: {
    namePlaceholder: 'e.g. Pricing keyword reply',
    nameNeeded: 'Give it a name to open the editor.',
    title: 'New automation',
    scratch: 'Start from scratch',
    scratchAside: 'Visual editor · for tailor-made automations',
    nameLabel: 'Automation name',
    open: 'Open the editor',
    creating: 'Creating…',
    fromTemplate: 'Or start from a template',
    seeAll: 'See all {{count}} templates →',
  },
  goal: {
    tagNeeded: 'Choose a tag to save.',
  },
  builderTest: {
    title: 'Test with a real contact',
    warning: 'Runs the <b>published version</b> for this contact now, through the real engine: messages are really sent and waits really wait. A run already in progress for them may be replaced.',
    unpublished: 'Your edits since publishing are not in the test until you publish them.',
    search: 'Search contacts',
    group: 'Test contact',
    loading: 'Loading contacts',
    noMatch: 'No contact matches.',
    none: 'No contacts yet.',
    run: 'Run test',
    runFor: 'Run test for {{name}}',
    running: 'Running…',
    error: 'Something went wrong. Please try again.',
    finished_one: 'Finished — {{count}} step ran.',
    finished_other: 'Finished — {{count}} steps ran.',
    waitingUntil_one: 'Waiting until {{time}} — {{count}} step so far.',
    waitingUntil_other: 'Waiting until {{time}} — {{count}} steps so far.',
    waitingContact_one: 'Waiting for the contact — {{count}} step so far.',
    waitingContact_other: 'Waiting for the contact — {{count}} steps so far.',
    failed: 'Failed: {{error}}',
    failedAt: 'Failed at {{step}}: {{error}}',
    canceled: 'Canceled before it finished.',
    stillRunning_one: 'Still running — {{count}} step so far.',
    stillRunning_other: 'Still running — {{count}} steps so far.',
    nothing: 'Nothing started for this contact: they may already be in a run of this automation, or the workspace has reached its monthly run limit.',
    seeRuns: 'See runs',
    enableFirst: 'Enable the automation first — a test runs the published version.',
    pickContact: 'Pick a contact to test with.',
  },
  builderBar: {
    neverPublished: 'Never published',
    upToDate: 'Up to date: the live version is the one you see',
    liveIsOlder: 'The live version is still the previous one',
    test: 'Test run',
    enable: 'Enable',
    reactivate: 'Reactivate',
    working: 'Working…',
    publish: 'Publish',
    publishChanges: 'Publish changes',
    publishing: 'Publishing…',
    nothingToPublish: 'No changes to publish',
    moreActions: 'More actions',
    disable: 'Disable',
    checksTitle: 'Checks',
    readyTitle: 'Ready to enable?',
    toCheck_one: '{{count}} point to check',
    toCheck_other: '{{count}} points to check',
  },
  builderConfirm: {
    enableTitle: 'Enable “{{name}}”?',
    reactivateTitle: 'Reactivate “{{name}}”?',
    disableTitle: 'Disable “{{name}}”?',
    createdViaAi: 'Draft created by AI — review it before publishing',
    disableBody: 'It stops answering your customers until you reactivate it.',
    trigger: 'Trigger',
    noTrigger: 'No trigger set up',
    steps: 'Steps',
    stepCount_one: '{{count}} step',
    stepCount_other: '{{count}} steps',
    openChecks_one: '{{count}} check is still open:',
    openChecks_other: '{{count}} checks are still open:',
    consequence: 'Once enabled, it answers real customers with the version you see.',
    reactivateConsequence: 'Reactivating it also publishes your unpublished changes: the version you see answers real customers.',
    cancel: 'Cancel',
    anyway: 'Enable anyway',
    disable: 'Disable',
    liveTitle: 'Your automation is live',
    liveBody: 'It answers from now on.',
    seeConversations: 'See conversations',
    disabledTitle: 'Automation disabled',
    disabledBody: 'It no longer answers until you reactivate it.',
  },
  triggerChannels: {
    unsupported_one: '{{only}} only: untick {{channels}}, which can’t start this automation.',
    unsupported_other: '{{only}} only: untick {{channels}}, which can’t start this automation.',
  },
  builderFields: {
    webhookUrlPlaceholder: 'e.g. https://api.example.com/leads',
    firstNameFallbackPlaceholder: 'e.g. there',
  },
  /** Builder blocks: the palette, the canvas nodes and the step settings (R06–R08). */
  nodes: {
    kind: { trigger: 'Trigger', condition: 'Condition', action: 'Action' },
    keyword_received: { label: 'Keyword received', description: 'A message matches one of your keywords' },
    message_received: { label: 'Message received', description: 'Any inbound message' },
    comment_received: { label: 'Comment received', description: 'An inbound comment — on any post, or one specific post' },
    story_reply_received: { label: 'Story reply received', description: 'Someone replied to one of your stories — any story, or one specific story' },
    story_mention_received: { label: 'Story mention', description: 'Someone mentioned you in their story' },
    live_comment_received: { label: 'Live comment', description: 'A comment during one of your lives — any, or one with a keyword' },
    contact_created: { label: 'New contact', description: 'A contact is created' },
    tag_added: { label: 'Tag added', description: 'A tag is added to a contact' },
    tag_removed: { label: 'Tag removed', description: 'A tag is removed from a contact' },
    manual_trigger: { label: 'Manual trigger', description: 'Started by a teammate' },
    inbound_webhook: { label: 'Webhook', description: 'Started by an external HTTP call' },
    send_message: { label: 'Send message', description: 'Reply on the conversation' },
    reply_comment: { label: 'Reply to comment', description: 'Reply publicly to the triggering comment' },
    react_to_message: { label: 'React with a heart', description: 'A heart on the message that started the automation' },
    add_tag: { label: 'Add tag', description: 'Tag the contact' },
    remove_tag: { label: 'Remove tag', description: 'Untag the contact' },
    set_custom_field: { label: 'Set custom field', description: 'Write a value to a field' },
    send_email: { label: 'Send email', description: 'Email the contact' },
    wait: { label: 'Wait', description: 'Pause before continuing' },
    http_request: { label: 'HTTP request', description: 'Call an external API' },
    goto_automation: { label: 'Go to automation', description: 'Continue in another automation' },
    stop: { label: 'Stop', description: 'End the automation here' },
    has_tag: { label: 'Has tag', description: 'Does the contact have this tag?' },
    not_has_tag: { label: 'Does not have tag', description: 'Is the contact missing this tag?' },
    field_equals: { label: 'Field equals', description: 'Does a field equal a value?' },
    field_contains: { label: 'Field contains', description: 'Does a field contain text?' },
    field_greater_than: { label: 'Field greater than', description: 'Is a numeric field above a value?' },
    field_less_than: { label: 'Field less than', description: 'Is a numeric field below a value?' },
    channel_is: { label: 'Channel', description: 'Did this arrive on a given channel?' },
    has_previous_interaction: { label: 'Previous interaction', description: 'Has the contact interacted before?' },
    follows_account: { label: 'Follows you', description: 'Does the contact follow your connected account?' },
    message_contains_keyword: { label: 'Message contains keyword', description: 'Does the triggering message contain a keyword?' },
  },
  /** What a step still needs, and why a block cannot go somewhere (D-105, D-141). */
  nodeProblems: {
    keyword: 'Add at least one keyword',
    tag: 'Choose a tag',
    field: 'Choose a field',
    number: 'Enter a number to compare with',
    value: 'Enter a value to compare with',
    messageText: 'Write the message text',
    replyText: 'Write the reply text',
    customField: 'Choose a custom field',
    subject: 'Add a subject',
    body: 'Write the email body',
    wait: 'Set how long to wait',
    url: 'Enter a full http(s) URL',
    automation: 'Choose the target automation',
    channel: 'Choose a channel',
    needsMessageTrigger: 'Works only after a DM, keyword, story reply or story mention trigger',
    channelOnly: '{{only}} only. This automation runs on {{channels}}.',
    channelGap: '{{only}} only: on {{missing}}, it always answers No. Put a “Channel is” check before it, or narrow the trigger.',
  },
  /** The second line of a canvas node. */
  nodeSummary: {
    post: 'post {{id}}',
    story: 'story {{id}}',
  },
  builderDuration: {
    seconds_one: '{{count}} second',
    seconds_other: '{{count}} seconds',
    minutes_one: '{{count}} minute',
    minutes_other: '{{count}} minutes',
    hours_one: '{{count}} hour',
    hours_other: '{{count}} hours',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
  },
  /** The automation editor (R06–R08): page, top bar and toasts. */
  builderPage: {
    loading: 'Loading automation',
    loadError: 'This automation could not be loaded. It may have been deleted.',
    backToList: 'Back to automations',
    back: 'Automations',
    name: 'Automation name',
    fixIssues: 'Fix the highlighted issues before enabling this automation.',
    changeFailed: 'The change failed',
    publishedTitle: 'Changes published',
    publishedBody: 'New triggers now run the version you just saved.',
    unsavedTitle: 'Your last changes were not saved',
    unsavedBody: 'The connection dropped while saving.',
    renameFailed: 'Could not rename the automation',
    tryAgain: 'Please try again.',
    inspector: 'Checks and test',
    stepPanel: 'Step settings',
    deletedStep: 'a deleted step',
  },
  builderPalette: {
    label: 'Blocks',
    search: 'Search blocks',
    close: 'Close blocks',
    hint: 'Drag a block onto the canvas, or click “+” between two steps.',
    groups: {
      trigger: 'Triggers',
      condition: 'Conditions',
      action: 'Actions',
    },
    triggersFolded: 'Folded: this automation already has its trigger.',
    noMatch: 'No block matches “{{query}}”.',
    unavailable: '{{label}} — {{reason}}',
  },
  builderCanvas: {
    label: 'Canvas',
    zoom: 'Zoom',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fit: 'Fit to screen',
    minimap: 'Mini map',
    addStep: 'Add a step',
    addStepBetween: 'Add a step between {{from}} and {{to}}',
    aStep: 'a step',
    needsAttention: 'Needs attention: {{problems}}',
    yes: 'Yes',
    no: 'No',
    next: 'Next',
    noTap: 'No tap',
    button: 'Button',
    nodeLabel: '{{kind}}: {{title}}',
    edgeLabel: 'From “{{from}}” to “{{to}}”',
    edgeLabelBranch: 'From “{{from}}” to “{{to}}”, {{branch}}',
    skipTo: 'Skip to the canvas',
    a11y: {
      node: 'Press Enter or Space to pick a step, Delete to remove it, arrow keys to move it.',
      nodeNoKeys: 'Press Enter or Space to pick a step.',
      edge: 'Press Enter or Space to pick a connection, Delete to remove it.',
      handle: 'Connection point',
      moved: 'Step moved {{direction}}. Position: x {{x}}, y {{y}}',
      directions: { up: 'up', down: 'down', left: 'left', right: 'right' },
    },
  },
  /** The editor on a phone: the steps as a list, edited through sheets (D-135). */
  builderFlow: {
    hint: 'Tap a step to edit it. To connect or move steps, use the visual editor.',
    openEditor: 'Open the editor',
    addTrigger: 'Add a trigger',
    notConnected: 'Not connected',
    steps: 'Automation steps',
    edit: 'Edit {{title}}',
    editNeeds: 'Edit {{title}} — needs attention: {{problems}}',
    addBefore: 'Add a step before {{title}}',
    addAfter: 'Add a step after {{title}}',
    ifYes: 'If yes',
    ifNo: 'If no',
    ifTap: 'If they tap “{{label}}”',
    theButton: 'the button',
    ifNoTap: 'If nobody taps',
    stepsButton: 'Steps',
    addStep: 'Add step',
    noFit: 'No step fits here.',
  },
  /** The checklist beside the canvas: Vérifications / Prête à activer ? (R06–R08). */
  builderChecks: {
    hint: 'Click a block to configure it.',
    done: '(done)',
    todo: '(to do)',
    addTrigger: 'Add a trigger',
    addTriggerDetail: 'Drag one in from the left — it decides when this automation runs.',
    needsSetup: '{{name}} needs setup',
    triggerOk: 'Trigger configured',
    stray: '{{name}} isn’t connected',
    strayDetail: 'Connect it to the automation or delete it — as it is, it never runs.',
    connected: 'Every step is connected',
    stepsAfter_one: '{{count}} step after the trigger',
    stepsAfter_other: '{{count}} steps after the trigger',
    branches: 'Yes → {{yes}} · No → {{no}}',
    end: 'end',
    needsAttention: '{{name}} needs attention',
    needsFallback: '{{name}} needs a fallback',
    fallbackDetail: 'Contacts without a {{fields}} would see an empty gap.',
    channelOk: 'Channel connected',
    connect: 'Connect a channel',
    connectDetail: 'Nothing reaches this automation until a channel is connected.',
  },
  /** A selected step’s settings. */
  builderConfig: {
    title: 'Step title',
    delete: 'Delete step',
    tag: 'Tag',
    chooseTag: 'Choose a tag…',
    keywords: 'Keywords',
    onePerLine: 'One per line',
    channels: 'Channels',
    channelsHint: 'Leave all unchecked to match every channel',
    fieldSource: 'Field source',
    contactField: 'Contact field',
    customField: 'Custom field',
    field: 'Field',
    chooseField: 'Choose a field…',
    value: 'Value',
    buttons: 'Buttons (max 3)',
    buttonLabel: 'Button {{n}} label',
    buttonLabelPlaceholder: 'e.g. Book a call',
    buttonLink: 'Button {{n}} link',
    buttonLinkPlaceholder: 'e.g. https://example.com',
    buttonLinkHint: 'Leave empty to branch the automation.',
    removeButton: 'Remove button',
    addButton: 'Button',
    buttonBranch: 'A button without a link becomes a branch you can connect on the canvas.',
    match: 'Match',
    matchExact: 'Message equals keyword',
    matchContains: 'Message contains keyword',
    matchStartsWith: 'Message starts with keyword',
    liveHint: 'Leave empty to answer every live comment. A keyword matches anywhere in the comment.',
    replyText: 'Reply text',
    replyHint: 'Posted publicly under the comment that triggered this automation. Only runs after a comment trigger.',
    contactCreatedNote: 'Runs whenever a new contact is created in this workspace. Nothing to configure.',
    manualNote: 'Started by a teammate from a contact page. Nothing to configure.',
    webhookNote: 'Starts when an external system calls this automation’s webhook URL (available once enabled).',
    messageText: 'Message text',
    subject: 'Subject',
    body: 'Body',
    choose: 'Choose…',
    yes: 'Yes',
    no: 'No',
    method: 'Method',
    url: 'URL',
    requestBodyHint: 'Optional. Variables are allowed.',
    continueIn: 'Continue in',
    chooseAutomation: 'Choose an automation…',
    stopNote: 'Ends the automation for this contact. Nothing to configure.',
    channel: 'Channel',
    withinDays: 'Within the last (days)',
    withinDaysHint: 'Leave empty for any time',
    reactNote: 'Puts a heart on the message that started this automation — a DM, a keyword or a story reply. Instagram allows the heart only, and cannot react to comments.',
    followsNote: 'When the automation reaches this step, the channel is asked whether the contact follows your account. Instagram answers; a channel that cannot say counts as “no”.',
    unknown: 'Unknown step type.',
  },
  builderEditors: {
    contactFields: {
      first_name: 'First name',
      last_name: 'Last name',
      full_name: 'Full name',
      email: 'Email',
      phone: 'Phone',
      username: 'Username',
    },
    waitFor: 'Wait for',
    waitLess: 'Wait less',
    waitMore: 'Wait more',
    waitDuration: 'Wait duration',
    unit: 'Unit',
    minutes: 'Min',
    hours: 'Hours',
    days: 'Days',
    waitSentence: 'The contact waits {{duration}} before the next step.',
    variable: 'Variable',
    insertVariable: 'Insert a variable',
    contactGroup: 'Contact',
    customFieldsGroup: 'Custom fields',
    fallbackIf: 'Fallback if {{field}} is empty',
    preview: 'Preview',
    previewEmpty: 'Your message appears here.',
    /** A made-up contact the preview shows where the text uses a name. */
    sample: { first_name: 'Sarra', last_name: 'Ben Ali', full_name: 'Sarra Ben Ali' },
    sampleNote: 'Preview for a made-up contact, {{name}}.',
  },
  /** The comment and story-reply triggers pinned to one post or story (D-103, D-117). */
  builderPins: {
    account: 'Account',
    idPlaceholder: 'e.g. 17900000000000000',
    onlyPost: 'Only this post',
    postHintNoChannel: 'Connect a channel to pick from its posts, or paste a post/media ID. Empty means comments on any post.',
    postHintCannotList: 'This channel cannot list posts — paste the post/media ID instead. Empty means comments on any post.',
    orScheduled: 'Or a scheduled post',
    orScheduledHint: 'The automation goes live when the post is published.',
    none: 'None',
    postHint: 'React only to comments on the selected post. A scheduled post goes live when it is published. “Any post” matches comments everywhere.',
    loadingPosts: 'Loading posts…',
    anyPost: 'Any post',
    scheduledGroup: 'Scheduled — live when published',
    publishedGroup: 'Published',
    currentlyPinned: 'Currently pinned: {{id}}',
    deletedScheduled: 'Deleted scheduled post',
    untitled: 'Untitled post',
    unpinned: 'Unpinned: this trigger now answers comments on every post of the account.',
    pinLoading: 'Loading the post…',
    pinDeleted: 'The scheduled post was deleted, so this trigger will never fire.',
    pinLoadError: 'The post could not be loaded: {{error}}',
    pinPublished: 'Published — answering its comments.',
    pinPublishedAt: 'Published {{at}} — answering its comments.',
    pinNoId: 'Published, but its id was not recorded, so its comments cannot be matched. Pin the published post instead.',
    pinPublishing: 'Publishing now — goes live in a moment.',
    pinFailed: 'The post could not be published — retry it or pick another post, or this trigger will never fire.',
    pinWaiting: 'Goes live when the post is published.',
    pinWaitingAt: 'Goes live when the post is published ({{at}}).',
    openPost: 'Open the post',
    onlyStory: 'Only this story',
    storyHintNoChannel: 'Connect a channel to pick from its stories, or paste a story ID. Empty means replies to any story.',
    storyHintCannotList: 'This channel cannot list stories — paste the story ID instead. Empty means replies to any story.',
    storyHint: 'React only to replies to the selected story. Stories last 24 hours; “Any story” keeps answering after this one expires.',
    loadingStories: 'Loading stories…',
    anyStory: 'Any story',
    storyGone: 'The pinned story is not among the stories up on this account, so nobody can reply to it any more. Pick one that is up, or “Any story”.',
    videoStory: 'Video story',
    story: 'Story',
  },
  builderUndo: {
    steps_one: 'Step deleted',
    steps_other: '{{count}} steps deleted',
    connections_one: 'Connection deleted',
    connections_other: '{{count}} connections deleted',
    hint: '{{keys}} also undoes it.',
    undo: 'Undo',
  },
  starters: {
    questionPlaceholder: 'e.g. What are your opening hours?',
    menuItemPlaceholder: 'e.g. Prices',
    back: 'Channels',
    title: 'Conversation starters & menu',
    lede: 'Give people a one-tap way to start talking, and a menu they can open any time.',
    startersTitle: 'Conversation starters',
    startersBody: 'Shown to someone opening a chat with you for the first time. A tap starts the automation you pick.',
    menuTitle: 'Menu',
    menuBody: 'Always available in the chat. Each entry starts an automation or opens a link.',
    getStarted: 'Messenger shows a Page’s menu only with a Get Started button, so publishing a menu adds one.',
    countLabel: '{{count}} of {{max}} used',
    starter: {
      text: 'Starter {{n}} title',
      automation: 'Starter {{n}} automation',
      more: 'More actions for starter {{n}}',
      handle: 'Move starter {{n}}',
      add: 'Add starter',
      none: 'No starters yet.',
      missing: 'Without an automation, this button does nothing.',
      moved: 'Starter moved to position {{n}} of {{total}}.',
    },
    menuItem: {
      text: 'Menu item {{n}} title',
      action: 'Menu item {{n}} action',
      automation: 'Menu item {{n}} automation',
      link: 'Menu item {{n}} link',
      more: 'More actions for menu item {{n}}',
      handle: 'Move menu item {{n}}',
      add: 'Add menu item',
      none: 'No menu items yet.',
      missing: 'Without an automation, this entry does nothing.',
      moved: 'Menu item moved to position {{n}} of {{total}}.',
    },
    starts: 'starts',
    opens: 'opens a link',
    chooseAutomation: 'Choose an automation…',
    notEnabled: '{{name}} (not enabled)',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove',
    dragHint: 'Drag to reorder, or use the arrow keys',
    atMost: '{{app}} shows at most {{max}}.',
    preview: 'Preview',
    previewIn: 'Preview in {{app}}',
    beforeFirst: 'Before the first message',
    noStarters: 'No conversation starters',
    noMenu: 'No menu',
    unlinked: 'Not linked',
    unpublished: 'Unpublished changes',
    lastPublished: 'Last published {{when}}',
    notPublished: 'Not published yet',
    noChangesShort: 'no changes',
    discard: 'Discard',
    publish: 'Save and publish',
    publishing: 'Publishing…',
    published: 'Published to {{app}}',
    refused: 'Saved, but {{app}} refused it',
    lastRefused: '{{app}} refused the last update: {{error}}',
    unsupported: 'This channel has no conversation starters or menu.',
  },
  channels: {
    title: 'Channels',
    lede: 'The accounts your automations listen and reply on.',
    catalog: {
      instagram: 'DMs, story replies and comment automations for a professional account.',
      messenger: 'Conversations with people who message your Facebook Page.',
    },
    other: 'Other connected accounts',
    connect: 'Connect',
    redirecting: 'Redirecting…',
    connectAnother: {
      instagram: 'Connect another Instagram account',
      messenger: 'Connect another Page',
      other: 'Connect another account',
    },
    since: 'Since {{date}}',
    pageSince: 'Facebook Page · since {{date}}',
    grant: 'Reconnect to grant {{list}}.',
    and: '{{first}} and {{last}}',
    accessExpired: 'Access expired {{date}}',
    accessExpires: 'Access expires {{date}}',
    renew: 'Renew',
    reconnect: 'Reconnect',
    disconnect: 'Disconnect',
    startersMenu: 'Starters & menu',
    actionsFor: 'Actions for {{name}}',
    planLimit_one: '{{count}} channel connected of the {{max}} included in the {{plan}} plan',
    planLimit_other: '{{count}} channels connected of the {{max}} included in the {{plan}} plan',
    seePlan: 'See plan',
    adminsOnly: 'Only workspace admins can connect or disconnect channels. You can see what is connected.',
    notConfigured: 'This channel is not configured on this server yet — the app credentials are missing from the deployment environment. See docs/DEPLOYMENT.md §11.',
    connected: '{{channel}} connected',
    connectedBody: 'Incoming messages on this account now reach your inbox and automations.',
    connectError: {
      title: 'Could not connect {{channel}}',
      denied: 'The access was not granted. Click Connect and accept the requested permissions to continue.',
      state_invalid: 'This connection link is not valid. Click Connect to start again.',
      state_expired: 'The connection took more than 10 minutes. Click Connect to start again.',
      state_foreign: 'This connection was started from another account or workspace. Click Connect to start your own.',
      no_account: 'No account that can be connected was found. Use an Instagram professional account or a Facebook Page you manage.',
      provider: '{{channel}} refused the authorization. Click Connect to try again.',
      in_use: 'This account is already connected to another workspace. Disconnect it there first.',
      limit: 'Your plan has no room for another channel.',
      failed: 'The connection did not go through. Click Connect to try again.',
    },
    disconnected: 'Channel disconnected',
    opening: 'Opening {{channel}}…',
    openingBody: 'The permission page opens in a few seconds. Please do not tap the screen until it does: on a phone or tablet, a tap would hand the page to the {{channel}} app instead of your browser, and every tap restarts the wait.',
    cancel: 'Cancel',
    disconnectTitle: 'Disconnect {{name}}?',
    disconnectBody: 'New messages to this account will stop reaching your inbox and automations. Existing conversations and contacts stay. You can reconnect at any time.',
    disconnecting: 'Disconnecting…',
    picker: {
      title: {
        messenger: 'Which Pages do you want to connect?',
        other: 'Which accounts do you want to connect?',
      },
      list: {
        messenger: 'Pages to connect',
        other: 'accounts to connect',
      },
      body: {
        messenger: 'Each Page you tick becomes a channel. You can connect the others later.',
        other: 'Each account you tick becomes a channel. You can connect the others later.',
      },
      count: {
        messenger_one: '{{count}} Page',
        messenger_other: '{{count}} Pages',
        other_one: '{{count}} account',
        other_other: '{{count}} accounts',
      },
      done: {
        messenger_one: '{{count}} Page connected',
        messenger_other: '{{count}} Pages connected',
        other_one: '{{count}} account connected',
        other_other: '{{count}} accounts connected',
      },
      expired: 'This {{channel}} authorization is no longer available. It lasts 10 minutes. Click Connect again to choose.',
      close: 'Close',
      connectedBody: 'Incoming messages now reach your inbox and automations.',
      noRoom: 'Your plan has no room for another channel.',
      room_one: 'Your plan has room for {{count}} more channel.',
      room_other: 'Your plan has room for {{count}} more channels.',
      untick: 'Untick {{what}} or',
      seePlan: 'see your plan',
      cancel: 'Cancel',
      connecting: 'Connecting…',
      connect: 'Connect',
      connectSome: 'Connect {{what}}',
      heldElsewhere: 'Connected in another workspace',
      connectedHere: 'Already connected',
    },
  },
  settings: {
    title: 'Settings',
    lede: 'Team, inbox behaviour, contact data, billing and developer access.',
    ledeFor: 'Team, inbox behaviour, contact data, billing and developer access for {{name}}.',
    loading: 'Loading',
    adminsOnly: 'Only admins can change this.',
  },
  developers: {
    adminsOnly: 'Only admins can create, see and revoke API tokens.',
    createTitle: 'Create an API token',
    intro: 'A token lets your AI assistant connect to this workspace. It acts with the rights of the person who created it, never more.',
    name: 'Token name',
    namePlaceholder: 'e.g. Claude Code on my laptop',
    nameNeeded: 'Enter a name for the token.',
    scopes: 'Permissions',
    scopeNeeded: 'Tick at least one permission.',
    scope: {
      read: 'Read',
      write: 'Write',
    },
    scopeHint: {
      read: 'Read channels, automations and their stats.',
      write: 'Create draft automations, only while your role is admin at the time of the call. A draft waits for your review before anything is published.',
    },
    lifetime: 'Valid for',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    create: 'Create token',
    creating: 'Creating…',
    limitReached: 'At most 10 active tokens: revoke one to create another.',
    createFailed: 'The token could not be created. Try again.',
    listTitle: 'API tokens',
    list: 'Workspace tokens',
    empty: 'No tokens yet.',
    createdBy: 'Created by {{name}} on {{date}}',
    lastUsed: 'Last used on {{date}}',
    neverUsed: 'Never used',
    expiresOn: 'Expires on {{date}}',
    expiredOn: 'Expired on {{date}}',
    revokedOn: 'Revoked on {{date}}',
    state: {
      active: 'Active',
      expired: 'Expired',
      revoked: 'Revoked',
    },
    revoke: 'Revoke',
    revokeLabel: 'Revoke the token {{name}}',
    revokeTitle: 'Revoke “{{name}}”?',
    revokeBody: 'The assistant using this token loses access at once. This cannot be undone.',
    revokeConfirm: 'Revoke token',
    revokeFailed: 'The token could not be revoked. Try again.',
    cancel: 'Cancel',
    created: {
      title: 'Token created',
      label: 'Your new token',
      warning: 'This token will not be shown again. Copy it now.',
      copy: 'Copy',
      copied: 'Copied',
      copyFailed: 'Copying failed: select the token and copy it.',
      done: 'I have copied the token',
    },
    connect: {
      title: 'Connect your AI assistant',
      intro: 'Your assistant connects to Repliq over MCP, with a token created on this page. In the examples, replace the placeholder with that token.',
      serverUrl: 'Server URL',
      copyUrl: 'Copy URL',
      copy: 'Copy',
      copied: 'Copied',
      copyFailed: 'Copying failed: select the text and copy it.',
      claudeCode: {
        title: 'Claude Code',
        cli: 'In a terminal:',
        json: 'Or in a project’s .mcp.json file, with the token in the REPLIQ_MCP_TOKEN environment variable:',
        noOAuth: 'With an Authorization header configured, Claude Code uses this token and does not fall back to OAuth.',
      },
      api: {
        title: 'Anthropic Messages API (MCP connector)',
        body: 'Add this beta header and these two fields to your request to /v1/messages:',
        limits: 'Only tool calls are supported, and the server URL must be public and start with https://.',
      },
      mcpRemote: {
        title: 'Claude Desktop and JSON-configured clients (mcp-remote)',
        body: 'mcp-remote bridges a local client to this server. Put the header in a one-line file, to keep the token out of the process arguments:',
        file: 'Header file contents',
        config: 'Client configuration (claude_desktop_config.json for Claude Desktop)',
        windows: 'On Windows, Claude Desktop does not escape spaces in arguments. The variant mcp-remote documents, with no spaces around the colon:',
      },
      connectors: {
        title: 'Custom connectors in claude.ai and Claude Desktop',
        step1: 'In Claude, open Customize › Connectors, add a custom connector and enter the server URL.',
        step2: 'Authentication: choose No sign-in.',
        step3: 'Request headers: add the header below. Claude sends the value exactly as entered, with no scheme added: keep “Bearer” and the space.',
        headerName: 'Name',
        headerValue: 'Value',
        beta: 'Beta: request header authentication is available to a limited set of organizations. If you do not see the Request headers section, your organization does not have access yet.',
        shared: 'On a Team or Enterprise plan, an Owner adds the connector for the whole organization. Every member then uses the same token, and so acts with the rights of the person who created it in Repliq.',
        oauth: 'Without access to this beta, claude.ai and Claude Desktop require OAuth for a remote connector.',
      },
      oauthV2: 'OAuth (rpqo_): planned for v2, needed for non-technical clients.',
    },
  },
  people: {
    emailPlaceholder: 'e.g. teammate@company.com',
    inviteTitle: 'Invite a teammate',
    email: 'Email address',
    role: 'Role',
    roles: { owner: 'Owner', admin: 'Admin', agent: 'Agent' },
    send: 'Send invite',
    sending: 'Sending…',
    emailNeeded: 'Enter an email address to invite.',
    hint: {
      agent: 'Agents work the inbox and contacts. They can’t edit automations, channels or billing.',
      admin: 'Admins also build automations, connect channels, invite people and see invoices.',
    },
    sent: 'Invitation sent to {{email}}',
    members: 'Members',
    membersOf_one: '{{count}} of {{max}} members',
    membersOf_other: '{{count}} of {{max}} members',
    membersCount_one: '{{count}} member',
    membersCount_other: '{{count}} members',
    seatsOf_one: '{{count}} of {{max}} inbox seats',
    seatsOf_other: '{{count}} of {{max}} inbox seats',
    seatsCount_one: '{{count}} inbox seat',
    seatsCount_other: '{{count}} inbox seats',
    seePlan: 'See plan',
    you: '(you)',
    seat: 'Inbox seat',
    pendingTitle: 'Pending invitations',
    noPending: 'No pending invitations.',
    invitedAs: 'Invited as {{role}} · expires {{date}}',
    revoke: 'Revoke',
    revokeLabel: 'Revoke the invitation to {{email}}',
    /** Every seat taken (members + pending invitations, D-183): the form is blocked before sending. */
    seatsFull_one: 'Your {{planName}} plan is limited to {{limit}} user. Free up a seat or upgrade to invite someone.',
    seatsFull_other: 'Your {{planName}} plan is limited to {{limit}} users. Free up a seat or upgrade to invite someone.',
  },
  inboxSettings: {
    title: 'Pause automations when a teammate replies',
    saved: 'Saved automatically',
    body: 'When a teammate answers from the inbox, automations step back so the bot never talks over them. Replies sent from the Instagram or Messenger app don’t count. Closing the conversation, or Resume in the inbox, lifts the pause at once.',
    length: 'Pause length',
    never: 'Don’t pause',
    default: '{{label}} (default)',
    minutes: '{{count}} min',
    hours_one: '{{count}} hour',
    hours_other: '{{count}} hours',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    week: '1 week',
    example: 'Example: if a teammate replies at 14:00, automations stay silent for that contact until {{until}}.',
    exampleNever: 'Replies don’t pause anything: automations keep answering alongside your team.',
    nextDay: '{{time}} the next day',
    weekLater: '{{time}} a week later',
    daysLater: '{{time}} {{count}} days later',
    saveFailed: 'Couldn’t save the pause length',
  },
  tags: {
    namePlaceholder: 'e.g. New lead',
    nameNeeded: 'Type a name to create the tag.',
    createTitle: 'Create a tag',
    name: 'Tag name',
    create: 'Create tag',
    creating: 'Creating…',
    created: 'Tag “{{name}}” created',
    preview: 'Preview',
    colour: 'Colour',
    colours: { slate: 'Slate', violet: 'Violet', blue: 'Blue', green: 'Green', amber: 'Amber', pink: 'Pink', red: 'Red', teal: 'Teal', orange: 'Orange' },
    list: 'Tags',
    contacts_one: '{{count}} contact',
    contacts_other: '{{count}} contacts',
    usedIn_one: 'used in {{count}} automation',
    usedIn_other: 'used in {{count}} automations',
    rename: 'Rename',
    renameLabel: 'Rename {{name}}',
    delete: 'Delete',
    deleteLabel: 'Delete {{name}}',
    emptyTitle: 'No tags yet',
    emptyBody: 'Tags like “New lead” or “VIP” filter contacts and steer automation conditions.',
    deleteTitle: 'Delete “{{name}}”?',
    deleteContacts_one: 'The tag is removed from {{count}} contact.',
    deleteContacts_other: 'The tag is removed from {{count}} contacts.',
    deleteAutomations_one: 'The automation using it will stop matching it.',
    deleteAutomations_other: 'The {{count}} automations using it will stop matching it.',
    cannotUndo: 'This cannot be undone.',
    cancel: 'Cancel',
    deleting: 'Deleting…',
    confirmDelete: 'Delete tag',
    deleted: 'Tag deleted',
    deletedBody: 'It was removed from every contact that had it.',
    editTitle: 'Rename tag',
    save: 'Save',
    saving: 'Saving…',
  },
  fields: {
    labelPlaceholder: 'e.g. Interested product',
    optionsPlaceholder: 'e.g. Basic\nPremium',
    nameNeeded: 'Type a name to add the field.',
    /** The key preview no longer comes from the placeholder (R34). */
    keyPending: 'The technical key is generated when you type a name.',
    addTitle: 'Add a field',
    label: 'Field label',
    add: 'Add field',
    adding: 'Adding…',
    type: 'Type',
    types: {
      text: 'Text',
      number: 'Number',
      date: 'Date',
      select: 'Dropdown',
      boolean: 'Yes / No',
      email: 'Email',
      phone: 'Phone',
    },
    hints: {
      text: 'Free-form text, e.g. a company name.',
      number: 'Numbers you can compare in conditions, e.g. a budget.',
      date: 'A date, e.g. a renewal or a birthday.',
      select: 'One option from a list you define.',
      boolean: 'A simple flag.',
      email: 'An email address, checked when it is saved.',
      phone: 'A phone number, checked when it is saved.',
    },
    key: 'key: {{key}}',
    keyNoLetters: 'The key is made from the label’s letters and digits: add some.',
    keyLetterFirst: 'A key starts with a letter: begin the label with a word.',
    keyReserved: '“{{key}}” is a built-in contact attribute: pick another label.',
    options: 'Options',
    optionsHint: 'One option per line.',
    optionsNeeded: 'Add at least one option to add this field.',
    adminsOnly: 'Only admins can change custom fields.',
    list: 'Custom fields',
    archived: 'Archived',
    edit: 'Edit {{label}}',
    more: 'More for {{label}}',
    archive: 'Archive',
    restore: 'Restore',
    delete: 'Delete',
    emptyTitle: 'No custom fields yet',
    emptyBody: 'Store structured data on contacts.',
    emptyStart: 'Start with a common one:',
    emptyAdmin: 'An admin can add the first one.',
    suggestions: { company: 'Company', budget: 'Budget', product: 'Interested product' },
    suggest: 'Add {{label}}',
    deleteTitle: 'Delete “{{label}}”?',
    deleteBody: 'This permanently deletes the field and its value on every contact. To only hide it from forms, archive it instead: archived fields keep their data.',
    cancel: 'Cancel',
    deleting: 'Deleting…',
    confirmDelete: 'Delete field and data',
    editTitle: 'Edit field',
    keyLabel: 'Key',
    keyHint: 'Keys are permanent: variables and imports refer to them.',
    editOptionsHint: 'One per line. Options still set on contacts cannot be removed.',
    save: 'Save',
    saving: 'Saving…',
  },
  usage: {
    plan: 'Current plan',
    title: 'Usage',
    aside: 'The limits that block first',
    atLimit: 'At limit',
    overLimit: 'Over limit',
    atLimitCount_one: '{{count}} limit at its maximum',
    atLimitCount_other: '{{count}} limits at their maximum',
    headroom: 'Room left',
    unlimited: 'Unlimited',
    meters: {
      contacts: 'Contacts',
      users: 'Users',
      inboxSeats: 'Inbox seats',
      channels: 'Connected channels',
      automations: 'Automations',
      activeAutomations: 'Active automations',
      customFields: 'Custom fields',
      executions: 'Triggers this month',
      messages: 'Messages this month',
    },
    usersHint_one: '{{users}} active + {{count}} pending invite',
    usersHint_other: '{{users}} active + {{count}} pending invites',
  },
  billing: {
    due: 'due {{date}}',
    overdue: 'overdue since {{date}}',
    invoiceLine: 'Invoice {{number}} · {{amount}}',
    howToPay: 'How to pay',
    howToPayNote: 'Mention the invoice number with your payment. This line goes away once we have recorded it.',
    invoices: 'Invoices',
    noInvoices: 'No invoices yet.',
    columns: { number: 'Invoice', period: 'Period', due: 'Due', amount: 'Amount', status: 'Status', pdf: 'PDF' },
    left: '{{amount}} left',
    paidOn: 'paid {{date}}',
    status: { issued: 'To pay', overdue: 'Overdue', paid: 'Paid', void: 'Canceled' },
    pdf: 'PDF',
    downloadPdf: 'Download PDF',
    downloading: 'Downloading…',
    pdfUnavailable: 'PDF unavailable — retry',
    pdfFor: '{{label}}: invoice {{number}}',
    downloadFailed: 'The download failed. Check your connection and try again.',
  },
  myPlan: {
    interval: { month: 'per month', quarter: 'per quarter', year: 'per year' },
    complimentaryName: '{{plan}} · Complimentary',
    status: {
      trialing: 'Trial until {{date}}',
      active: 'Active · renews on {{date}}',
      complimentary: 'Active',
      complimentaryUntil: 'Active · until {{date}}',
      pastDue: 'Payment overdue',
      suspended: 'Suspended',
    },
    suspension: {
      trialTitle: 'Your Pro trial has ended',
      complimentaryTitle: 'Your complimentary plan has ended',
      trialBody: 'Your channels, automations and conversations are stopped. Choose a plan below: your workspace resumes once your payment is confirmed.',
      otherTitle: 'Your workspace is suspended',
      otherBody: 'Your channels, automations and conversations are stopped. Your plan and invoices stay available here.',
    },
    request: {
      pendingTitle: 'Waiting for your payment',
      pendingBody: 'You asked for the {{plan}} plan ({{price}}). The payment instructions were sent to you by email; the plan applies once the payment arrives.',
      paymentInstructions: 'How to pay',
      scheduledTitle: 'Moves to {{plan}} on {{date}}',
      scheduledBody: 'Your current plan stays until then, with no refund.',
      cancel: 'Cancel the request',
      cancelTitle: 'Cancel the request?',
      cancelBody: 'The request for the {{plan}} plan will be withdrawn. You can make another one afterwards.',
      keep: 'Keep the request',
      canceled: 'Request canceled',
      cancelFailed: 'The request could not be canceled. Try again.',
    },
    compare: {
      title: 'Change plan',
      billing: 'Billing',
      intervals: { month: 'Monthly', quarter: 'Quarterly', year: 'Yearly' },
      current: 'Current plan',
      highlighted: 'Selected plan',
      choose: 'Choose this plan',
      chooseLabel: 'Choose the {{plan}} plan',
      ownerOnly: 'Only the owner can change the plan.',
      limit: '{{label}}: {{value}}',
      empty: 'No plan is offered right now.',
    },
    confirm: {
      title: 'Move to {{plan}}',
      price: '{{price}} {{interval}}',
      upgradeBody: 'The payment instructions will reach you by email. The {{plan}} plan applies as soon as your payment arrives.',
      downgradeBody: 'The change takes effect on {{date}}, at the end of the period already paid, with no refund.',
      creditCovers: 'Your remaining credit of {{credit}} covers {{plan}} until {{date}}.',
      amountDue: 'Amount to pay: {{amount}}.',
      coveredBody: 'The {{plan}} plan applies as soon as our team confirms your request.',
      coveredAtOnceBody: 'The {{plan}} plan applies right away, with no payment to wait for.',
      activate: 'Activate the plan',
      send: 'Send the request',
      back: 'Back',
      sentUpgrade: 'Request sent',
      sentDowngrade: 'Change scheduled',
      pending: 'A request is already open for this workspace. The page has just been updated.',
      failed: 'The request could not be sent. Try again.',
    },
    creditActive: 'Your {{plan}} plan is active. Your remaining credit covers it until {{date}}: nothing to pay.',
    usageSuspended: 'Usage is not shown while the workspace is suspended. It comes back once the workspace is reactivated.',
  },
  planLock: {
    lockLabel: 'Locked',
    title: {
      scheduledPosts: 'Scheduled posts and stories.',
      campaigns: 'Campaigns.',
      segments: 'Segments.',
      httpRequestStep: 'HTTP request step.',
      apiAccess: 'API tokens and MCP server.',
    },
    availableFrom: 'Available from {{plan}}.',
    notIncluded: 'Not included in your plan.',
    askOwner: 'Not included in your plan. Ask the workspace owner.',
    seePlans: 'See plans',
  },
  planBlock: {
    featureTitle: 'Not included in your plan',
    limitTitle: 'Plan limit reached',
    feature: {
      scheduledPosts: 'Scheduled posts and stories are not included in your plan.',
      campaigns: 'Campaigns are not included in your plan.',
      segments: 'Segments are not included in your plan.',
      httpRequestStep: 'The HTTP request step is not included in your plan.',
      apiAccess: 'API tokens and the MCP server are not included in your plan.',
      analyticsHistory: 'Your plan keeps {{days}} days of analytics. Pick a shorter period or change plan.',
      other: 'This feature is not included in your plan.',
    },
    limit: '{{label}}: your plan’s limit ({{limit}}) is reached.',
    limitOther: 'A limit of your plan is reached.',
    change: 'Change plan',
    askOwner: 'Ask the workspace owner.',
  },
  suspended: {
    trialTitle: 'Your Pro trial has ended',
    complimentaryTitle: 'Your complimentary plan has ended',
    otherTitle: 'This workspace is suspended',
    body: 'Channels, automations and conversations are stopped.',
    ownerBody: 'Choose a plan to reactivate the workspace.',
    choose: 'Choose a plan',
    agentBody: 'Contact the workspace owner.',
  },
  upsell: {
    includedIn: 'Included in {{plan}}',
    notIncludedPill: 'Not included in your plan',
    notOffered: 'Not included in the plans on offer.',
    previewLabel: 'Preview · Example',
    availableWith: 'Available with {{plan}}',
    alsoTitle: 'Moving to {{plan}} also unlocks',
    compare: 'Compare all plans →',
    seeMyPlan: 'See My plan',
    per: { month: '/month', quarter: '/quarter', year: '/year' },
    upgrade: 'Move to {{plan}}',
    upgradeWithPrice: 'Move to {{plan}} · {{price}}{{interval}}',
    context: {
      current: 'You are on {{current}} ({{price}}{{interval}}).',
      currentPlain: 'You are on {{current}}.',
      trial: 'You are on a {{current}} trial until {{date}}.',
      activation: '{{plan}} is on as soon as your payment is confirmed.',
      ownerOnly: 'Only the workspace owner can change the plan.',
    },
    request: {
      pending: 'Request to move to {{plan}} sent — waiting for your payment',
      scheduled: 'Change to {{plan}} scheduled on {{date}}',
    },
    ask: {
      action: 'Ask the owner',
      sent: 'Request sent to the owner',
      alreadySent: 'Already asked on {{date}}',
      noOwner: 'No active owner to notify for this workspace.',
      failed: 'The request could not be sent. Try again.',
    },
    bar: {
      label: 'Upgrade to {{plan}}',
      path: '{{current}} → {{plan}}',
    },
    unlocks: {
      scheduledPosts: { title: 'Scheduled posts and stories', body: 'Prepare your posts and stories ahead: they go out at the planned time.' },
      campaigns: { title: 'Campaigns', body: 'Launch a series of posts in one go, once you have checked it.' },
      segments: { title: 'Segments', body: 'Follow up only with the people who asked for the price without ordering.' },
      httpRequestStep: { title: 'HTTP request in automations', body: 'Send the orders received by DM to your shop or your spreadsheet.' },
      apiAccess: { title: 'API tokens and MCP server', body: 'Connect your AI assistant to read your numbers and prepare automation drafts.' },
    },
    limits: {
      users: '{{users}} users',
      usersUnlimited: 'Unlimited users',
      days: '{{days}} days of analytics',
      daysUnlimited: 'Unlimited history',
      body: 'The whole team on the inbox, and {{days}} days of history.',
      bodyUnlimited: 'The whole team on the inbox, and the whole history.',
    },
    days: { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sun: 'Sun' },
    speakers: { you: 'You', assistant: 'Claude' },
    tags: { post: 'Post', story: 'Story', photo: 'Photo', carousel: 'Carousel', reel: 'Reel', video: 'Video', read: 'Read', write: 'Write' },
    variants: {
      campaigns: {
        title: 'Prepare a week of posts in one go.',
        body: 'No more posts published one by one at the last minute. Compose your launch, check the calendar at a glance, then let Repliq publish at the planned time.',
        benefit1: 'Posts and stories scheduled together, on Instagram and Facebook',
        benefit2: 'Nothing goes out before you approve: you launch when everything is ready',
        benefit3: 'Ideal for a collection launch, a sale or a promo code',
        previewTitle: 'New collection — launch week',
        previewMeta: '5 posts · Monday → Sunday · Instagram',
        previewBadge: 'Ready to launch',
        action: 'Launch the campaign',
        more: '+ 2 more posts',
        rows: {
          r1: 'Teaser: “Something is coming…”',
          r2: 'Collection reveal',
          r3: 'Carousel: 6 looks worn',
          r4: 'Promo code for followers only',
          r5: 'Last pieces in stock',
        },
      },
      posts: {
        title: 'Schedule your posts, Repliq publishes them for you.',
        body: 'Write your posts when you have the time, pick the day and the hour: Repliq publishes them on time on Instagram and Facebook, without you having to think about it.',
        benefit1: 'Photos, carousels and Reels scheduled for the day and time you choose',
        benefit2: 'A copy on your Facebook Page, at the same time as on Instagram',
        benefit3: 'Each post’s numbers once it is live: likes, comments, reach',
        previewTitle: 'Back-to-school week',
        previewMeta: '5 posts · Monday → Friday · Instagram',
        previewBadge: 'Upcoming',
        action: 'Schedule the post',
        more: '+ 2 more posts',
        rows: {
          r1: 'This week’s new arrivals',
          r2: 'Behind the scenes at the workshop',
          r3: '5 ways to wear it',
          r4: 'A customer’s review',
          r5: 'The week in review',
        },
      },
      stories: {
        title: 'Keep your stories going all week.',
        body: 'A story stays up for 24 hours only. Prepare the whole series in one go: Repliq publishes each one on time, on Instagram and Facebook.',
        benefit1: 'One photo or video per story, published at the time you choose',
        benefit2: 'The whole week prepared in one go, published day after day',
        benefit3: 'Views, reach, replies and shares for each story during its 24 hours',
        previewTitle: 'Sale countdown',
        previewMeta: '5 stories · Monday → Friday · Instagram',
        previewBadge: 'Upcoming',
        action: 'Schedule the story',
        more: '+ 2 more stories',
        rows: {
          r1: 'D-4: the sale is coming',
          r2: 'Behind the scenes of the preparation',
          r3: 'A look at the sale items',
          r4: 'Tomorrow is the big day',
          r5: 'It’s on: up to 30% off',
        },
      },
      developers: {
        title: 'Drive Repliq from your AI assistant.',
        body: 'Create an API token and connect Claude to your workspace over MCP. Ask it for your numbers, or to prepare an automation you review before publishing it.',
        benefit1: 'Your channels, automations and their numbers, read by your assistant',
        benefit2: 'Automation drafts prepared for you, published only after your review',
        benefit3: 'Tokens with an expiry date, revocable at any time',
        previewTitle: 'Claude — connected over MCP',
        previewMeta: 'Token rpq_••••••••3f9a · Read and write',
        previewBadge: 'Active',
        action: 'Create a token',
        more: '+ 2 more exchanges',
        rows: {
          r1: 'How many triggers this week?',
          r2: '1,284 triggers, 312 of them for “Price by DM”.',
          r3: 'Prepare an automatic reply to “price” comments.',
          r4: 'Draft “Price reply” created: review it before publishing.',
          r5: 'Which channels are connected?',
        },
      },
    },
  },
  segments: {
    namePlaceholder: 'e.g. VIPs who messaged this month',
    new: 'New segment',
    edit: 'Edit segment',
    name: 'Name',
    nameNeeded: 'Give the segment a name.',
    matching: 'Contacts matching',
    match: 'Match',
    matchAll: 'all',
    matchAny: 'any',
    ofConditions: 'of these conditions',
    types: { tag: 'Tag', field: 'Custom field', channel: 'Channel', status: 'Status', created: 'Added', last_interaction: 'Last interaction' },
    part: {
      type: 'Condition {{n}} type',
      operator: 'Condition {{n}} operator',
      tag: 'Condition {{n}} tag',
      field: 'Condition {{n}} field',
      value: 'Condition {{n}} value',
      channel: 'Condition {{n}} channel',
      status: 'Condition {{n}} status',
      days: 'Condition {{n}} days',
    },
    ops: {
      is: 'is',
      isNot: 'is not',
      chooseTag: 'Choose a tag…',
      chooseField: 'Choose a field…',
      isSet: 'has any value',
      isNotSet: 'is empty',
      eq: 'is',
      gt: 'is more than',
      lt: 'is less than',
      contains: 'contains',
      within: 'in the last',
      older: 'more than',
      never: 'never',
      days: 'days',
      daysAgo: 'days ago',
    },
    remove: 'Remove condition {{n}}',
    add: '+ Condition',
    finish: 'Finish the conditions to see who matches.',
    count_one: '{{count}} contact matches',
    count_other: '{{count}} contacts match',
    counting: 'Counting…',
    cancel: 'Cancel',
    save: 'Save segment',
    create: 'Create segment',
  },
  composerFields: {
    captionPlaceholder: 'e.g. New in store! Comment PRICE to get the link. Mention people with @username.',
    hashtagPlaceholder: 'e.g. #promo',
    collaboratorPlaceholder: 'e.g. @partner_brand',
    usernamePlaceholder: 'e.g. @username',
  },
  postPin: {
    cancelled: 'The post was cancelled: schedule it again or pick another post, or this trigger will never fire.',
  },
  composer: {
    summary: {
      plannedFor: 'Planned for {{when}}',
      now: 'Publishes as soon as you confirm',
      pickTime: 'Pick a date and time',
    },
    toast: {
      scheduledFor: 'Scheduled for {{when}}',
      plannedFor: 'Planned for {{when}}',
      draftSaved: 'Draft saved',
      publishingNow: 'Publishing now',
      saveFailed: 'Couldn’t save the draft',
    },
    title: {
      post: { new: 'New post', edit: 'Edit post', scheduled: 'Scheduled post' },
      story: { new: 'New story', edit: 'Edit story', scheduled: 'Scheduled story' },
    },
    unsaved: 'Not saved',
    back: { post: 'Posts', story: 'Stories' },
    view: { label: 'View', edit: 'Edit', preview: 'Preview' },
    account: 'Account',
    accountOption: '{{name}} (cannot publish)',
    openChannels: 'Open Channels',
    goToChannels: 'Go to Channels',
    noAccount: 'Connect an Instagram account or a Facebook Page first.',
    format: 'Format',
    caption: 'Caption',
    content: 'Content',
    counters: {
      characters: '{{value}} / {{max}} characters',
      hashtags: '{{value}} / {{max}} hashtags',
      mentions: '{{value}} / {{max}} mentions',
    },
    hashtags: 'Hashtags',
    hashtagsHint: 'Added at the end of the caption.',
    hashtagInvalid: '#{{tag}} is not a valid hashtag: letters, numbers and underscores only.',
    collaborators: 'Collaborators',
    collaboratorsMax: '· {{max}} accounts max',
    collaboratorsHint: 'They’re invited to share it on their profile.',
    collaboratorInvalid: '@{{name}} is not a valid Instagram username.',
    shareToFeed: 'Also show the Reel in the main feed',
    campaign: {
      partOf: 'Part of the campaign',
      holds: {
        post: 'The post is saved with its planned time and goes out when the campaign is launched.',
        story: 'The story is saved with its planned time and goes out when the campaign is launched.',
      },
      live: {
        post: 'The campaign is live, so the post is scheduled as soon as you save it.',
        story: 'The campaign is live, so the story is scheduled as soon as you save it.',
      },
    },
    when: {
      label: 'When',
      schedule: 'Schedule',
      now: 'Publish now',
      date: 'Date',
      time: 'Time',
      pickDate: 'Pick a date',
      pickTime: 'Pick a time',
      holds: 'The planned time: the post goes out then, once the campaign is launched.',
      zoneSentence: 'Workspace time zone, {{zone}}. It goes out at that time wherever you are.',
      leavesIn: 'Goes out in {{span}}',
    },
    sameDay: {
      none: 'That day: no other post or story scheduled.',
      label: 'That day: {{list}}.',
      posts_one: '{{count}} other post',
      posts_other: '{{count}} other posts',
      stories_one: '{{count}} other story',
      stories_other: '{{count}} other stories',
    },
    stats: {
      drafts_one: 'Draft',
      drafts_other: 'Drafts',
      scheduled_one: 'Scheduled',
      scheduled_other: 'Scheduled',
    },
    actions: 'Save',
    ready: {
      title: 'Ready to go',
      detail: '{{channels}} · {{when}}',
      withPage: '{{platform}} + {{page}}',
      now: 'as soon as you confirm',
    },
    cta: {
      post: 'Schedule post',
      story: 'Schedule story',
      saveChanges: 'Save changes',
      publishNow: 'Publish now',
      saveToCampaign: 'Save to campaign',
      saving: 'Saving…',
    },
    secondary: {
      draft: 'Save as draft',
      unschedule: 'Unschedule (back to draft)',
      noTime: 'Save without a time',
    },
    problems: 'Problems to fix',
    need: {
      image: 'Add a photo',
      carousel: 'Add photos or videos',
      reel: 'Add a video',
      story: 'Add a photo or video',
    },
    gate: {
      pickAccount: 'Pick an account to post from.',
      upload: 'Wait for the upload to finish.',
      campaign: 'Loading the campaign…',
      post: {
        schedule: '{{need}} to schedule this post.',
        publish: '{{need}} to publish this post.',
        plan: '{{need}} to plan this post.',
      },
      story: {
        schedule: '{{need}} to schedule this story.',
        publish: '{{need}} to publish this story.',
        plan: '{{need}} to plan this story.',
      },
    },
    unsavable: {
      loading: 'Loading',
      pickAccount: 'Pick an account',
      connect: 'Connect an Instagram account or a Facebook Page',
      upload: 'Waiting for the upload',
      campaign: 'Loading the campaign',
    },
    note: {
      applySchedule: 'Changes apply when you schedule',
      applyPublish: 'Changes apply when you publish',
      applySave: 'Changes apply when you save them',
      notSaved: 'Not saved yet · {{reason}}',
    },
    blockers: {
      account: 'This account cannot publish.',
      page: 'This Page cannot publish.',
      pageReason: '{{page}}: {{reason}}',
      canceledCampaign: 'This campaign is stopped. Its posts cannot be changed.',
      onCopy: 'On {{page}}: {{issue}}',
    },
    schedule: {
      pickDateTime: 'Pick a date and time.',
      future: 'Pick a time in the future.',
      futureOrNow: 'Pick a time in the future, or choose Publish now.',
    },
    nothingToSave: 'Nothing to save',
    preview: {
      label: 'Preview',
      story: 'Story preview',
      media: 'Media preview',
      caption: 'Your caption appears here.',
      more: 'more',
      sendMessage: 'Send message',
      previous: 'Previous item',
      next: 'Next item',
      account: 'your.account',
      on: 'Preview on',
    },
  },
  composerMedia: {
    drop: {
      image: {
        empty: 'Drop a photo here or click to upload',
        more: 'Drop another photo to replace it',
        hint: 'JPEG, PNG or WebP · we convert and crop to 4:5–1.91:1',
        hintPage: 'JPEG, PNG or WebP · up to 4 MB',
        hintBoth: 'JPEG, PNG or WebP · up to 4 MB · cropped to 4:5–1.91:1',
      },
      carousel: {
        empty: 'Drop photos here or click to upload',
        more: 'Drop more photos or videos, or click to add',
        morePage: 'Drop more photos, or click to add',
        hint: '2 to 10 photos or videos · videos up to 60 s · photos cropped to 4:5–1.91:1',
        hintPage: '2 to 10 photos — Facebook takes no videos here',
        hintBoth: '2 to 10 photos — Facebook takes no videos here · cropped to 4:5–1.91:1',
      },
      reel: {
        empty: 'Drop a video here or click to upload',
        more: 'Drop another video to replace it',
        hint: 'MP4 or MOV, 3 s to 15 min, up to 300 MB · vertical 9:16 looks best',
        hintPage: 'MP4 or MOV, 3 to 90 s · vertical 9:16 looks best',
      },
      story: {
        empty: 'Drop a photo or video here or click to upload',
        more: 'Drop another photo or video to replace it',
        hint: 'JPEG, PNG or WebP, or MP4/MOV 3–60 s up to 100 MB · shown as is, vertical 9:16 looks best',
      },
    },
    chooseFile: 'Choose a file',
    addMedia: 'Add media',
    list: 'Media',
    tag: 'Tag people',
    tagShort: 'Tag',
    alt: 'Alt text',
    altShort: 'Alt text',
    describe: 'Describe the photo',
    remove: 'Remove',
    moveLeft: 'Move item {{n}} left',
    moveRight: 'Move item {{n}} right',
    deleteItem: 'Delete item {{n}}',
    tagged_one: '{{count}} tagged',
    tagged_other: '{{count}} tagged',
    dismiss: 'Dismiss',
    uploading: 'Uploading {{name}}',
    full: 'That’s {{max}} — the most a carousel takes.',
    note: '{{name}}: {{note}}',
    refused: {
      image: 'not a photo, so it was skipped.',
      carousel: 'not a photo or an MP4/MOV video, so it was skipped.',
      reel: 'not an MP4 or MOV video, so it was skipped.',
      story: 'not a photo or an MP4/MOV video, so it was skipped.',
    },
    skipped_one: '1 file was skipped: a carousel holds {{max}} items.',
    skipped_other: '{{count}} files were skipped: a carousel holds {{max}} items.',
    onlyOne: 'Only {{name}} was used: this format takes one file.',
    cover: {
      title: 'Cover image',
      hint: 'Optional. Without one, Instagram uses the first frame.',
      hintBoth: 'Optional, Instagram only. Without one, Instagram uses the first frame; Facebook always picks its own.',
      change: 'Change',
      add: 'Add cover',
      input: 'Add cover image',
    },
    modal: {
      tag: 'Tag people and describe the photo',
      describe: 'Describe the photo',
      altHint: 'Describes the photo for people who use screen readers. Optional.',
      done: 'Done',
    },
    prepare: {
      unknown: 'Use a photo (JPEG, PNG, WebP) or a video (MP4, MOV).',
      coverPhoto: 'The cover must be a photo.',
      videoType: 'Use an MP4 or MOV video.',
      storySize: 'A story video can be at most 100 MB.',
      videoSize: 'Videos can be at most 300 MB.',
      reelPage: 'A Reel for Facebook must be between 3 and 90 seconds long.',
      reel: 'A Reel must be between 3 seconds and 15 minutes long.',
      storyLength: 'A story video must be between 3 and 60 seconds long.',
      carouselLength: 'A carousel video must be between 3 and 60 seconds long.',
      noCanvas: 'This browser cannot prepare the photo.',
      tooLarge: 'This photo is too large even after compression.',
      cannotOpen: 'This photo format cannot be opened in this browser.',
      croppedPortrait: 'Cropped to 4:5 portrait',
      croppedLandscape: 'Cropped to 1.91:1 landscape',
      converted: 'Converted to JPEG',
      resized: 'Resized',
    },
    tagPeople: {
      photo: 'Photo: click where the person is to tag them',
      removeTag: 'Remove tag @{{name}}',
      username: 'Username to tag',
      invalid: 'Enter an Instagram username: letters, numbers, dots and underscores.',
      already: '@{{name}} is already tagged on this photo.',
      submit: 'Tag',
      cancel: 'Cancel',
      full: '{{max}} people is the most Instagram allows on one photo.',
      help: 'Click on the photo where the person is. Tagged people are notified when the post goes live.',
    },
    chips: {
      remove: 'Remove {{value}}',
      max: 'At most {{max}}.',
    },
  },
  crossPost: {
    title: 'Also post on Facebook',
    post: 'The same post goes to your Page at the same time. Collaborators, people tags and the Reel cover stay on Instagram.',
    story: 'The same story goes up on your Page at the same time.',
    page: 'Page',
    lastTry: 'Last try on {{page}}: {{error}}',
    alsoOn: 'Also on Facebook',
    publishing: 'Being published on {{page}}. It stays as it is: changes here are not copied to it.',
    published: 'Already published on {{page}}. It stays as it is: changes here are not copied to it.',
    connect: {
      before: 'Want it on Facebook too?',
      link: 'Connect a Facebook Page',
      post: 'and the same post can go there at the same time.',
      story: 'and the same story can go there at the same time.',
    },
  },
  postAutomations: {
    title: 'Automations on this post',
    lede: 'They answer comments on this post only and switch on when it publishes.',
    quick: {
      auto_dm: { title: 'Comment → DM', body: 'DM a link to everyone who comments' },
      reply: { title: 'Reply to comment', body: 'Public thank-you under each comment' },
    },
    linked: {
      live: 'Live',
      goesLive: 'Goes live when published',
      archived: 'Archived',
    },
    open: 'Open',
    created: 'Created when you schedule the post or save it as a draft.',
    createdKept: 'Created when you schedule the post or save it as a draft. Until then they’re kept in this browser.',
    add: 'Add an automation',
    fold: {
      linked_one: '{{count}} linked',
      linked_other: '{{count}} linked',
      toCreate: '{{count}} to create',
      none: 'None',
    },
    summary: {
      specific: 'Comments with {{words}}',
      every: 'Every comment',
      publicReply: 'public reply',
      followers: 'followers only',
      dmLink: 'DM with a link',
      dm: 'DM',
      tag: 'tag “{{name}}”',
    },
    template: {
      label: 'Save as a template for your next posts',
      replaces: 'This replaces your template “{{name}}”.',
      failed: 'Could not save the template.',
      cancel: 'Cancel',
      saving: 'Saving…',
      replace: 'Replace template',
      save: 'Save template',
      saved: 'Saved as the template “{{name}}”.',
    },
    card: {
      on: 'Switched on with the post.',
      finishFirst: 'Finish the automation first',
      saveAsTemplate: 'Save as template',
      collapse: 'Collapse',
      edit: 'Edit',
      remove: 'Remove',
      removeLabel: 'Remove {{name}}',
      when: 'When the comment has',
      specific: 'a specific word or words',
      any: 'any word',
      then: 'Then',
      publicReply: 'reply publicly under the comment',
      publicReplyHint: 'Visible to everyone.',
      publicReplyField: 'Public reply',
      followersOnly: 'only followers get the DM',
      followersHint: 'Instagram tells us who follows you only after they tap a button in your DM, so they first get a message with a button.',
      opener: 'First message, with a button',
      buttonLabel: 'Button label',
      followersNote: 'When they tap it, followers get the DM below. Everyone else gets this, with an “{{button}}” button to check again:',
      askField: 'Message for people who do not follow you',
      sendFollowersDm: 'send followers a DM',
      sendDm: 'send a DM',
      dmHint: 'With your link, if you add one.',
      tag: 'tag them',
      tagHintDm: 'Added to everyone who gets the DM.',
      tagHintAll: 'Added to everyone who comments.',
      tagField: 'Tag name',
      tagPlaceholder: 'e.g. Giveaway entrant',
    },
    picker: {
      title: 'Add an automation to this post',
      lede: 'You set it up on the post; it answers comments on this post only, from the moment it is published.',
      mine: 'My templates',
      loading: 'Loading your templates…',
      noneBefore: 'None yet. Set up an automation on a post, then use',
      noneAfter: 'to reuse it here.',
      readyMade: 'Ready-made',
      custom: 'Custom',
      scratch: 'Start from scratch',
      scratchBody: 'Choose the steps yourself: public reply, followers only, DM, tag.',
      add: 'Add',
      delete: 'Delete',
      sure: 'Sure?',
      deleteLabel: 'Delete {{name}}',
      confirmDelete: 'Confirm deleting {{name}}',
    },
    presets: {
      auto_dm: { label: 'Auto-DM with a link', description: 'Everyone who comments gets your link in their DMs.' },
      reply: { label: 'Reply to comments', description: 'A public thank-you under every comment.' },
      giveaway: {
        label: 'Run a giveaway',
        description: 'A public “check your DMs”; followers are entered and tagged, everyone else is asked to follow.',
      },
      follow_gate: {
        label: 'Grow followers from comments',
        description: 'Followers get the link; everyone else is asked to follow first.',
      },
      dm_tag: { label: 'Comment → DM + tag', description: 'A DM to every commenter, tagged so you can reach them again.' },
      custom: { label: 'Custom automation', description: 'Start empty and choose the steps yourself.' },
    },
    problems: {
      replyOrDm: 'Turn on a public reply or a DM.',
      followersNeedDm: '“Only followers” needs a DM for the followers.',
      tagName: 'Name the tag, or turn it off.',
      tagLength: 'Keep the tag name under {{max}} characters.',
      opener: 'Write the first message, the one with the button.',
      openerLength: 'Keep the first message under {{max}} characters.',
      buttonLabel: 'Give the first message’s button a label.',
      buttonLength: 'Keep the button label under {{max}} characters.',
      prefixed: '{{name}}: {{problem}}',
    },
    outcome: {
      onNow_one: '1 automation is switched on: it goes live as soon as the post is up.',
      onNow_other: '{{count}} automations are switched on: they go live as soon as the post is up.',
      onLater_one: '1 automation is switched on: it goes live the moment the post is published.',
      onLater_other: '{{count}} automations are switched on: they go live the moment the post is published.',
      notOn: '“{{name}}” was created but is not switched on: {{reason}}.',
      failed: '“{{name}}” could not be created: {{reason}}.',
      retry: 'The post is saved; what could not be created stays on its edit page — save again to retry.',
      issues_one: 'it has {{count}} issue(s) to fix in the builder',
      issues_other: 'it has {{count}} issue(s) to fix in the builder',
      empty: 'it is empty — {{reason}}; fill it in the builder or delete it',
      unknown: 'Something went wrong',
    },
  },
  postIssues: {
    addReel: 'Add a video for the Reel.',
    addStory: 'Add a photo or video for the story.',
    addPhoto: 'Add a photo.',
    addMedia: 'Add a photo or video.',
    imageOne: 'An image post has exactly one image.',
    reelOne: 'A Reel has exactly one video.',
    storyOne: 'A story has exactly one photo or video.',
    multiPhotoCount: 'A multi-photo post has between {{min}} and {{max}} photos.',
    multiPhotoOnly: 'On Facebook, a multi-photo post holds photos only.',
    carouselCount: 'A carousel has between {{min}} and {{max}} photos or videos.',
    storySize: 'A story video can be at most 100 MB.',
    item: 'Item {{n}}',
    thePhoto: 'The photo',
    pagePhotoSize: '{{label}}: Facebook takes photos up to 4 MB.',
    tagsInstagramOnly: '{{label}}: people can be tagged on Instagram only.',
    tagsPhotosOnly: '{{label}}: people can only be tagged on photos.',
    ratio: '{{label}}: Instagram needs photos between 4:5 portrait and 1.91:1 landscape.',
    captionOver: 'The caption is over {{max}} characters.',
    collaboratorsInstagram: 'Collaborators are an Instagram feature.',
    hashtags: 'Use at most {{max}} hashtags.',
    mentions: 'Mention at most {{max}} accounts.',
    collaborators: 'Invite at most {{max}} collaborators.',
  },
  published: {
    title: {
      post: 'Published post',
      story: 'Published story',
      publishingPost: 'Post being published',
      publishingStory: 'Story being published',
    },
    note: {
      post: 'This post is live: it can no longer be edited here. Here is what went out, where, and how it is doing.',
      story: 'This story is published: it can no longer be edited. Here is what went out and how it is doing.',
      publishingPost: 'This post is being published: it can no longer be edited.',
      publishingStory: 'This story is being published: it can no longer be edited.',
    },
    viewOn: 'View on {{platform}}',
    back: { post: 'Back to posts', story: 'Back to stories' },
    preview: 'Preview',
    stats: 'Statistics',
    synced: 'Synced {{ago}}',
    pending: 'Numbers from Instagram arrive within 48 hours.',
    metric: {
      views: 'Views',
      reach: 'Reach',
      likes: 'Likes',
      comments: 'Comments',
      saved: 'Saves',
      replies: 'Replies',
      shares: 'Shares',
    },
    details: 'Details',
    detail: {
      format: 'Format',
      publishedOn: 'Published on',
      alsoOn: 'Also on',
      automations: 'Automations',
      none: 'None',
      account: 'Account',
      alsoOnFacebook: 'Also on Facebook',
      yes: 'Yes · {{page}}',
      no: 'No',
      original: 'Original',
      campaign: 'Campaign',
    },
    copy: {
      notePost: 'This post is the Facebook copy (Page {{page}}) of a post by {{original}}. It follows the original: edit the original and the copy updates.',
      noteStory: 'This story is the Facebook copy (Page {{page}}) of a story by {{original}}. It follows the original: edit the original and the copy updates.',
      editOriginal: 'Edit the original ({{account}})',
      noTime: 'no time yet',
      page: 'Page {{name}} (Facebook)',
      original: '{{account}} · {{platform}}',
    },
  },
  /** The date range above every set of figures (Dashboard R01, Analytics R29 and R30). */
  period: {
    label: 'Period',
    today: 'Today',
    d7: '7 days',
    d30: '30 days',
    custom: 'Custom',
    heading: {
      today: 'Today',
      d7: 'Over the last 7 days',
      d30: 'Over the last 30 days',
      custom: 'Over the chosen period',
    },
    from: 'Start date',
    to: 'End date',
    between: 'to',
    /** While a custom period is incomplete the figures stay on the previous one, as the heading says. */
    problem: {
      pickTwo: 'Pick two dates. Until then, the figures stay on the previous period.',
      order: 'The start date must come before the end date.',
      tooLong: 'Pick a period of 400 days at most.',
    },
    historyHint: 'Your plan keeps {{days}} days of analytics.',
    versus: {
      today: 'vs. yesterday',
      d7: 'vs. previous 7 days',
      d30: 'vs. previous 30 days',
      custom: 'vs. previous period',
    },
    same: {
      today: 'same as yesterday',
      d7: 'same as the previous 7 days',
      d30: 'same as the previous 30 days',
      custom: 'same as the previous period',
    },
  },
  /** Dashboard (R01). */
  dashboard: {
    greeting: {
      morning: 'Good morning',
      afternoon: 'Good afternoon',
      evening: 'Good evening',
    },
    greetingNamed: '{{greeting}}, {{name}}',
    lede: 'Here’s what’s running in {{workspace}}.',
    ledeSetup: 'Let’s get your workspace live.',
    /** The lede once the figures are in: does everything work? (critique 2026-10-29) */
    verdict: {
      failing_one: '{{count}} automation failed this period: it heads the list below.',
      failing_other: '{{count}} automations failed this period: they head the list below.',
      silent_one: 'No failures, but “{{name}}” is live and has not triggered for 30 days.',
      silent_other: 'No failures, but {{count}} live automations have not triggered for 30 days.',
      allGood_one: 'Everything works: {{count}} trigger this period, no failures.',
      allGood_other: 'Everything works: {{count}} triggers this period, no failures.',
      quiet: 'Nothing triggered this period.',
    },
    latestFailure: {
      named: 'Latest failure {{when}}: “{{name}}”.',
      deleted: 'Latest failure {{when}}, on an automation deleted since.',
      see: 'View',
    },
    yourWorkspace: 'your workspace',
    figures: 'Key figures',
    errorGeneric: 'Something went wrong. Please try again.',
    contacts: 'Contacts',
    newInPeriod_one: 'new this period',
    newInPeriod_other: 'new this period',
    noneNew: 'none new this period',
    importContacts: 'Import contacts →',
    messagesSent: 'Messages sent',
    nothingSent: 'nothing sent yet',
    inPeriod: 'this period',
    activeAutomations: 'Active automations',
    ofTotal: 'of {{total}}',
    currentState: 'right now',
    draftsLeft_one: '{{count}} draft to finish',
    draftsLeft_other: '{{count}} drafts to finish',
    startFromTemplate: 'Start from a template →',
    health: {
      title: 'Automation health',
      seeAll: 'View all',
      fired_one: '{{count}} trigger fired',
      fired_other: '{{count}} triggers fired',
      succeeded: 'successful',
      allSucceeded: 'all successful',
      failed_one: '{{count}} failed',
      failed_other: '{{count}} failed',
      inProgress: '{{count}} in progress',
      none: 'Nothing triggered this period',
      silent: 'Live, nothing triggered for 30 days',
      lastFailed: 'latest {{when}}',
      legend: 'Green = successful · grey = nothing to measure · red = a failure to fix',
      rate: 'Success rate: {{value}}',
      noRate: 'Nothing to measure',
      empty: 'No automations yet. Templates get you live in about two minutes.',
      browseTemplates: 'Browse templates',
    },
    token: {
      expires_one: '{{channel}} token for {{account}} expires in {{count}} day',
      expires_other: '{{channel}} token for {{account}} expires in {{count}} days',
      expired: '{{channel}} token for {{account}} has expired',
      renew: 'Renew',
    },
    conversations: {
      title: 'Recent conversations',
      open: 'Open',
      emptyTitle: 'No conversations yet',
      emptyBody: 'Messages from your channels show up here as soon as someone writes to you.',
      noMessages: 'No messages yet',
      link: 'Link · {{domain}}',
      unread_one: '{{count}} unread message',
      unread_other: '{{count}} unread messages',
    },
    activity: {
      title: 'Recent activity',
      grouped: 'Grouped by automation run',
      empty: 'Activity shows up here once your automations start running.',
    },
    setup: {
      title: 'Get {{workspace}} live',
      lede: 'Four steps to your first automated conversation.',
      progress: 'Setup progress',
      done_one: '{{count}} of {{total}} done',
      done_other: '{{count}} of {{total}} done',
      doneSr: '(done)',
      connect: 'Connect a channel',
      connectBody: 'Instagram or Messenger, in a few clicks',
      connected: '{{name}} connected',
      build: 'Build your first automation',
      buildBody: 'Pricing replies, welcome messages, follow-ups',
      import: 'Import contacts',
      importBody: 'A CSV with tags and custom fields',
      invite: 'Invite your team',
      inviteBody: 'Agents answer conversations, admins build automations',
    },
    /** "Prochaine mise en ligne" (Murmura M1, `P1-Dashboard.dc.html`). */
    next: {
      title: 'Next to go live',
      meta: '{{format}} · {{account}}',
      leavesIn: 'goes live in',
      crossPosted: '{{platform}} + {{page}}',
      open: 'Open',
      then: 'Up next',
      thenMeta: '{{date}} · {{time}} · {{account}}',
      nothingElse: 'Nothing else scheduled.',
      untimed_one: 'The post in {{name}} has no time yet.',
      untimed_other: 'The {{count}} posts in {{name}} have no time yet.',
    },
    /** "À traiter" (Murmura M1): what someone must act on, with the app's own actions. */
    todo: {
      title: 'To do',
      count_one: '{{count}} item',
      count_other: '{{count}} items',
      invoice: {
        title: 'Invoice {{number}} · {{amount}}',
        due: 'Due by {{date}}.',
        overdue: 'Overdue: it was due on {{date}}.',
        view: 'View invoice',
      },
      failed: {
        post_one: '{{count}} post failed',
        post_other: '{{count}} posts failed',
        story_one: '{{count}} story failed',
        story_other: '{{count}} stories failed',
        retry: 'Retry',
        view: 'View',
      },
      campaign: {
        untimed_one: '{{count}} post with no time: the campaign can’t be launched.',
        untimed_other: '{{count}} posts with no time: the campaign can’t be launched.',
        setTimes: 'Set the times',
      },
      automation: {
        notLive: 'Automation not live yet.',
        more_one: '+ {{count}} more draft automation',
        more_other: '+ {{count}} more draft automations',
        viewAll: 'View automations',
      },
      clear: {
        both: 'No failed posts · no unread conversations.',
        noFailures: 'No failed posts.',
        noUnread: 'No unread conversations.',
      },
      nothing: 'Nothing to do.',
    },
  },
  /** The activity log in plain words (Dashboard, R01): one row per automation run, its steps as chips. */
  activity: {
    someAutomation: 'An automation',
    someAutomationInline: 'an automation',
    aContact: 'a contact',
    aTag: 'a tag',
    aTeammate: 'A teammate',
    suspended: 'suspended',
    reactivated: 'reactivated',
    line: {
      started: '{{automation}} started for {{contact}}',
      completed: '{{automation}} finished for {{contact}}',
      failed: '{{automation}} failed for {{contact}}',
      canceled: '{{automation}} was stopped for {{contact}}',
      limitBlocked: '{{automation}} was held back by the plan’s monthly limit',
      retriggerIgnored: '{{automation}} was already running for {{contact}}',
      takeoverSuppressed: '{{automation}} stayed quiet while a teammate talked to {{contact}}',
      workspaceSuspended: '{{automation}} stopped: the workspace was suspended',
      repliedBy: '{{automation}} replied to {{contact}}',
      sent: 'A message was sent to {{contact}}',
      reacted: '{{automation}} reacted with a heart to {{contact}}',
      reactFailed: 'A heart reaction to {{contact}} could not be sent',
      commentReplied: '{{automation}} answered a comment from {{contact}}',
      emailSent: 'An email was sent to {{contact}}',
      tagged: '{{contact}} was tagged {{tag}}',
      tagAdded: 'Tag {{tag}} added',
      tagRemoved: '{{tag}} was removed from {{contact}}',
      agentReplied: 'A teammate replied to {{contact}}',
      closed: 'The conversation with {{contact}} was resolved',
      reopened: 'The conversation with {{contact}} was reopened',
      paused: 'Automations paused for {{contact}}',
      resumed: 'Automations resumed for {{contact}}',
      workspace: 'The workspace was {{state}}',
      contactErased: '{{member}} erased a contact',
    },
    /** One run on one row: "Maya Haddad — Lead Follow-up answered the comment, sent a message, then finished". */
    run: '{{contact}} — {{automation}} {{steps}}',
    runNoContact: '{{automation}} {{steps}}',
    then: ', then ',
    step: {
      started: 'started',
      commentReplied: 'answered the comment',
      sent: 'sent a message',
      reacted: 'reacted with a heart',
      emailSent: 'sent an email',
      tagAdded: 'added the tag {{tag}}',
      tagRemoved: 'removed the tag {{tag}}',
      completed: 'finished',
      failed: 'failed',
      canceled: 'was stopped',
      limitBlocked: 'was held back by the plan’s limit',
    },
    chip: {
      commentReplied: 'Comment handled',
      sent: 'Message sent',
      reacted: 'Reaction sent',
      emailSent: 'Email sent',
      tagAdded: 'Tag added',
      tagRemoved: 'Tag removed',
      limitBlocked: 'Plan limit',
    },
  },
  /** Analytics (R29). */
  analytics: {
    title: 'Analytics',
    lede: 'Triggers, messages and conversions per automation.',
    figures: 'Key figures',
    errorGeneric: 'Something went wrong. Please try again.',
    fired: 'Triggers fired',
    firedDetail: 'times an automation started',
    successRate: 'Success rate',
    successOf_one: '{{count}} successful out of {{finished}}',
    successOf_other: '{{count}} successful out of {{finished}}',
    noFinished: 'None finished yet',
    noRuns: 'Nothing triggered',
    failures_one: '{{count}} failed',
    failures_other: '{{count}} failed',
    messagesSent: 'Messages sent',
    messagesDetail: 'by your automations',
    reached: 'Contacts reached',
    reachedDetail: 'added up: one person can count in several automations',
    archived: 'Archived',
    chart: {
      title: 'Triggers fired per day',
      legend: 'Legend',
      succeeded: 'Successful',
      failed: 'Failed',
      loading: 'Loading the chart',
      partial: 'Adds up your {{count}} busiest automations.',
      ok_one: '{{count}} successful',
      ok_other: '{{count}} successful',
      peakFailed_one: '{{ok}} + {{count}} failed',
      peakFailed_other: '{{ok}} + {{count}} failed',
      noneOnDay: 'Nothing triggered',
      summary: '{{ok}} and {{failed}} over {{days}}.',
      peak: 'Busiest day: {{date}}, {{value}}.',
    },
    table: {
      title: 'By automation',
      label: 'Analytics by automation',
      noGoal: 'No automation has a conversion goal',
      automation: 'Automation',
      fired: 'Triggers fired',
      success: 'Success',
      messages: 'Messages',
      reached: 'Contacts reached',
      conversions: 'Conversions',
      setGoal: 'Set a goal',
      converted: '{{rate}} converted',
      footer: '“—”: not active yet · “0”: nothing sent this period',
    },
    empty: {
      title: 'Nothing triggered this period',
      body: 'Enable an automation and its triggers, messages and conversions will show up here.',
      action: 'Go to automations',
    },
  },
  /** One automation's analytics (R30). */
  automationAnalytics: {
    back: 'Analytics',
    fallbackTitle: 'Automation',
    lede: 'How this automation performed over the chosen period.',
    openEditor: 'Open in the editor',
    fired: 'Triggers fired',
    succeeded_one: '{{count}} successful',
    succeeded_other: '{{count}} successful',
    failed_one: '{{count}} failed',
    failed_other: '{{count}} failed',
    messagesSent: 'Messages sent',
    reached: 'Contacts reached',
    inPeriod: 'this period',
    conversions: 'Conversions',
    setGoal: 'Set a goal ↓',
    conversionRate: '{{rate}} of contacts reached',
    chart: {
      title: 'Activity per day',
      fired: 'Triggers fired',
      messages: 'Messages sent',
      noFired: 'Nothing triggered this period.',
      noMessages: 'No messages sent this period.',
      peak: '{{value}} · {{date}}',
      firedCount_one: '{{count}} trigger fired',
      firedCount_other: '{{count}} triggers fired',
      messagesCount_one: '{{count}} message sent',
      messagesCount_other: '{{count}} messages sent',
      summary: '{{fired}} and {{messages}} over {{days}}.',
    },
    goal: {
      title: 'Conversion goal',
      configured: 'Configured',
      notConfigured: 'Not configured',
      body: 'A trigger counts as converted when the contact receives the chosen tag within the window after it starts. Without a goal, conversions show as “—” rather than a misleading zero.',
      tag: 'Goal tag',
      chooseTag: 'Choose a tag…',
      window: 'Window',
      windowHint: 'From the moment the automation starts',
      w24: '1 day (24 h)',
      w72: '3 days (72 h)',
      w168: '1 week (168 h)',
      w720: '30 days (720 h)',
      save: 'Save goal',
      saving: 'Saving…',
      remove: 'Remove',
      saved: 'Conversion goal saved',
      removed: 'Conversion goal removed',
      saveFailed: 'Could not save the goal',
    },
  },
  automationsList: {
    title: 'Automations',
    lede: 'Automations that respond to messages, keywords and contact changes.',
    templates: 'Templates',
    new: 'New automation',
    tabsLabel: 'Filter by status',
    tabs: { all: 'All', active: 'Active', draft: 'Drafts', paused: 'Paused' },
    search: 'Search automations',
    tableLabel: 'Automations',
    columns: {
      automation: 'Automation',
      status: 'Status',
      fired: 'Triggers fired · 7 d',
      success: 'Success',
      last: 'Last triggered',
      actions: 'Actions',
    },
    origin: { quick: 'Quick', custom: 'Custom' },
    noTrigger: 'No trigger yet',
    edited: { ago: 'Edited {{when}}', today: 'Edited at {{when}}', date: 'Edited on {{when}}' },
    finishSetup: 'Finish setup',
    succeeded_one: 'successful',
    succeeded_other: 'successful',
    failed_one: '{{count}} failed',
    failed_other: '{{count}} failed',
    fired_one: '{{count}} trigger fired · 7 d',
    fired_other: '{{count}} triggers fired · 7 d',
    lastRun: 'Last triggered',
    moreActions: 'More actions for {{name}}',
    menu: {
      analytics: 'Analytics',
      pause: 'Pause',
      resume: 'Resume',
      enable: 'Enable',
      rename: 'Rename',
      duplicate: 'Duplicate',
      delete: 'Delete',
    },
    swipe: { pause: 'Pause' },
    footer: '“—”: nothing to measure · “0”: no trigger fired in 7 days',
    hint: 'To pause or resume, use the ⋯ menu on each row.',
    statsError: 'Trigger counts couldn’t load — the list itself is up to date.',
    loadError: 'Could not load automations.',
    errorGeneric: 'Something went wrong.',
    validation: '“{{name}}” has validation errors. Open it in the visual editor to fix them before enabling.',
    toast: {
      duplicated: 'Automation duplicated',
      opening: 'Opening “{{name}}”.',
      paused: '“{{name}}” paused',
      pausedBody: 'It stops matching new triggers immediately.',
      live: '“{{name}}” is live',
      liveBody: 'The published version now matches incoming triggers.',
      deleted: 'Automation deleted',
      deletedBody: 'Its trigger history is kept for analytics.',
    },
    deleteDialog: {
      title: 'Delete “{{name}}”?',
      body: 'The automation stops matching immediately. Its trigger history is kept for analytics.',
      cancel: 'Cancel',
      confirm: 'Delete automation',
    },
    renameDialog: {
      title: 'Rename automation',
      name: 'Name',
      cancel: 'Cancel',
      save: 'Save',
      failed: 'Rename failed.',
      nameNeeded: 'Enter a name to save.',
    },
    empty: {
      query: 'No automations match “{{query}}”.',
      all: 'No automations yet.',
      active: 'Nothing is live right now.',
      draft: 'No drafts.',
      paused: 'No paused automations.',
    },
    first: {
      title: 'Your first automation is two minutes away',
      body: 'Start from a proven template and tailor it — every template lands as an editable draft.',
    },
  },
  templates: {
    title: 'Templates',
    lede: 'Proven automations you can tailor — each one lands as an editable draft.',
    goalsLabel: 'Goal',
    all: 'All',
    goals: {
      grow: 'Grow followers',
      engage: 'Engage audience',
      traffic: 'Drive traffic',
      leads: 'Collect leads',
      sell: 'Sell more',
    },
    search: 'Search templates',
    trigger: 'Trigger',
    allTriggers: 'All triggers ({{count}})',
    triggerOption: '{{name}} ({{count}})',
    count_one: '{{count}} template',
    count_other: '{{count}} templates',
    effort: { quick: 'Quick · 2 min', custom: 'Visual editor' },
    use: 'Use',
    useLabel: 'Use template: {{name}}',
    listLabel: 'Templates',
    moreSteps_one: '{{count}} more step',
    moreSteps_other: '{{count}} more steps',
    steps: {
      comment_received: 'Comment',
      live_comment_received: 'Live',
      story_reply_received: 'Story',
      story_mention_received: 'Mention',
      keyword_received: 'Keyword',
      message_received: 'Message',
      contact_created: 'New contact',
      tag_added: 'Tag added',
      send_message: 'DM',
      reply_comment: 'Public reply',
      add_tag: 'Tag',
      wait: 'Wait',
      follows_account: 'Follows?',
      check: 'Check',
    },
    emptyTitle: 'No templates available',
    emptyBody: 'Built-in templates ship with the platform — if none appear, the server may be out of date.',
    noMatchTitle: 'No template matches',
    noMatchBody: 'Try another goal or trigger, or clear the search.',
    showAll: 'Show all templates',
    created: '“{{name}}” created',
    createdBody: 'It starts as a draft — review it and enable when ready.',
    createFailed: 'Could not create the automation.',
    detail: {
      dialogTitle: 'Template',
      label: '{{name}} details',
      startsOn: 'Starts on: {{trigger}}',
      aTrigger: 'a trigger',
      value: 'Business value',
      how: 'How does it work?',
      createsTags_one: 'Creates this tag if you don’t have it yet:',
      createsTags_other: 'Creates these tags if you don’t have them yet:',
      quickNote: 'Quick automation: a guided form, no canvas. You go live from the last step.',
      customNote: 'Visual editor: it lands as a draft in the editor. Review the messages, then switch it on.',
      back: 'Back to list',
      use: 'Use this template',
      settingUp: 'Setting up…',
    },
    items: {
      quick_comment_to_dm: {
        name: 'Auto-DM links from comments',
        description: 'Send a link when people comment on a post or reel. Pick the post, write the messages, go live — no canvas.',
        value: 'The fastest way to turn comments into clicks: set it up in a minute, without touching the visual editor.',
        how: 'Choose one post or any post, optionally the words a comment must contain, then write an optional public reply and the DM with your link button. When someone comments, they get the reply and the link in their DMs.',
      },
      quick_story_link: {
        name: 'Generate leads with stories',
        description: 'Send a link to everyone who replies to your story — any reply, or only ones with your keywords.',
        value: 'Story replies are your warmest audience: answer every one instantly with the offer they are reacting to.',
        how: 'Choose whether any reply counts or only replies with your words or reactions, then write the DM and add your link button. Everyone who replies to your story gets it straight away.',
      },
      quick_dm_link: {
        name: 'Respond to your DMs with a link',
        description: 'Auto-send a reply with your link when people DM you a keyword — or answer every message.',
        value: 'Never type the same link twice: people who ask get it in seconds, day or night.',
        how: 'Type the words that should trigger it (for example Price or Link), or choose any message, then write the reply and add your link button.',
      },
      welcome_new_lead: {
        name: 'Welcome New Lead',
        description: 'Greets every new contact and tags them as a lead.',
        value: 'Every new contact gets a warm hello within seconds, so first impressions never depend on whether you are online.',
        how: 'The moment someone becomes a contact — their first message, comment or import — they get a friendly welcome and the New Lead tag, so fresh leads are one filter away.',
      },
      keyword_pricing: {
        name: 'Keyword Pricing',
        description: 'Replies with pricing when someone messages “price”.',
        value: 'Stop typing your prices twenty times a day. People who ask get the answer instantly, while they are still interested.',
        how: 'When a message says “price”, the automation replies with your pricing and a button to see more, then tags the contact Interested so you know who to follow up with.',
      },
      lead_followup: {
        name: 'Lead Follow-up',
        description: 'Waits a day after tagging a lead, then follows up unless they purchased.',
        value: 'Leads that go quiet get a gentle nudge automatically, so no warm lead slips through the cracks.',
        how: 'When a contact is tagged New Lead, the automation waits a day. If they have not been tagged Purchased by then, it sends a friendly follow-up; if they bought, it stops.',
      },
      comment_to_dm: {
        name: 'Comment → DM',
        description: 'Sends a private message to anyone who comments on a chosen post, and tags them.',
        value: 'Turn comments into conversations: every comment on your post becomes a private chat you can sell in.',
        how: 'Pick a post. Anyone who comments gets a private message from you straight away and the Commented tag, so you can reach them again later.',
      },
      reply_to_comment: {
        name: 'Reply to Comment',
        description: 'Replies publicly under every comment. Pin it to one post in the trigger, or leave it open.',
        value: 'Your comment section looks active and cared for, even when you are busy creating.',
        how: 'Each new comment gets a public reply from your account, thanking the person and inviting them to check their DMs.',
      },
      story_reply_dm: {
        name: 'Story Reply → DM',
        description: 'Sends a DM to anyone who replies to one of your stories, and tags them.',
        value: 'Story replies are your warmest leads. Answer them instantly and keep the conversation going.',
        how: 'When someone replies to one of your stories, they get a message back straight away and the Story reply tag.',
      },
      follower_only_link: {
        name: 'Follower-only Link',
        description: 'When someone sends LINK, checks whether they follow you: followers get the link, everyone else is asked to follow first.',
        value: 'Reward your followers and turn every link request into a new follow.',
        how: 'When someone sends LINK, the automation checks whether they follow you. Followers get the link; everyone else is asked to follow first and send LINK again.',
      },
      default_reply: {
        name: 'Respond to all your DMs',
        description: 'Answers every direct message straight away, so nobody waits in silence while you are away.',
        value: 'Nobody waits in silence. Every message gets an instant answer that sets expectations.',
        how: 'Any incoming message gets an automatic reply saying you will answer soon. A reply from Conversations pauses automations for that contact, so it never talks over your team.',
      },
      giveaway: {
        name: 'Run a giveaway',
        description: 'Everyone who comments gets a public “check your DMs” reply and a DM to enter. Followers are entered and tagged.',
        value: 'Giveaways bring a flood of comments and new followers. This handles every entry for you, fairly and instantly.',
        how: 'Every commenter gets a public “Check your DMs” reply and a DM with an Enter button. When they tap it, the automation checks whether they follow you: followers are entered and tagged Giveaway entrant, everyone else is asked to follow and tap “I’m following”.',
      },
      comment_follow_gate: {
        name: 'Grow followers from comments',
        description: 'Someone comments for the link and taps the button in your DM: followers get the link, everyone else is asked to follow first.',
        value: 'Every comment asking for your link becomes a chance to win a new follower.',
        how: 'Someone comments on your post and gets a DM with a “Send me the link” button. When they tap it, followers get the link; everyone else is asked to follow first and tap “I’m following”.',
      },
      story_coupon: {
        name: 'Give coupons in stories',
        description: 'Replying to your story earns a secret discount code in DMs, and the contact is tagged for follow-ups.',
        value: 'Make story viewers feel like VIPs and turn attention into sales.',
        how: 'When someone replies to your story, they receive a secret discount code in their DMs and the Coupon sent tag, so you can see who got the offer.',
      },
      story_mention_thanks: {
        name: 'Thank people who mention you',
        description: 'When someone mentions you in their story, they get a thank-you DM with a small reward and a tag.',
        value: 'People who mention you are your best ambassadors. Thank them and give them a reason to do it again.',
        how: 'When someone mentions your account in their story, they get a thank-you message with a small reward and the Mentioned us tag.',
      },
      live_comment_link: {
        name: 'Send a link during your Live',
        description: 'Viewers who comment LINK during a Live get the link in their DMs, without you leaving the broadcast.',
        value: 'Catch buyers in the moment, without stopping your Live to paste links.',
        how: 'During a Live, anyone who comments LINK gets the link in a private message. Other live comments are left alone.',
      },
      collab_requests: {
        name: 'Answer collab requests',
        description: 'Messages mentioning a collab get your media-kit reply automatically, tagged so you can follow up.',
        value: 'Spend less time copy-pasting your media kit and more time closing collaborations.',
        how: 'When a message mentions collab, partnership or sponsor, the automation answers with your media-kit reply and tags the contact Collab request for follow-up.',
      },
    },
  },
  contacts: {
    title: 'Contacts',
    lede_one: '{{count}} person in this workspace',
    lede_other: '{{count}} people in this workspace',
    ledeEmpty: 'No one here yet',
    export: 'Export',
    exporting: 'Exporting…',
    exportFailed: 'Could not export contacts',
    importCsv: 'Import CSV',
    new: 'New contact',
    segmentsLabel: 'Segments',
    allContacts: 'All contacts',
    newSegment: 'New segment',
    segmentActions: '{{name}} segment actions',
    editSegment: 'Edit segment',
    deleteSegment: 'Delete segment',
    deleteSegmentFailed: 'Could not delete the segment',
    searchLabel: 'Search contacts',
    search: 'Name, username, email or phone',
    filters: {
      all: 'all',
      tag: 'Tag: {{value}}',
      channel: 'Channel: {{value}}',
      status: 'Status: {{value}}',
      tagLabel: 'Filter by tag',
      channelLabel: 'Filter by channel',
      statusLabel: 'Filter by status',
      field: 'Field',
      fieldValue: '{{field}} {{op}} {{value}}',
      fieldList: 'Field',
      condition: 'Condition',
      value: 'Value',
      valuePlaceholder: 'e.g. Paris',
      apply: 'Apply',
      ops: { eq: 'is', contains: 'contains', gt: 'greater than', lt: 'less than' },
    },
    statuses: { active: 'Active', unsubscribed: 'Unsubscribed', blocked: 'Blocked' },
    mockChannel: 'Mock (dev)',
    columns: { contact: 'Contact', tags: 'Tags', channel: 'Channel', last: 'Last activity', added: 'Added', email: 'Email' },
    selectAll: 'Select all on this page',
    select: 'Select {{name}}',
    listLabel: 'Contacts',
    noTags: 'No tags',
    noEmail: 'No email',
    pager: '{{from}}–{{to}} of {{total}}',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    unnamed: 'Unnamed contact',
    errorGeneric: 'Something went wrong. Please try again.',
    empty: {
      title: 'Bring your audience in',
      body: 'Import a CSV with tags and custom fields. New people also appear here automatically the first time they message you or comment.',
      import: 'Import CSV',
      manual: 'Add manually',
      sample: 'Download sample CSV',
      noMatchTitle: 'Nothing matches these filters',
      noMatchBody: 'Try widening the search or clearing a filter.',
      clear: 'Clear filters',
      segmentTitle: 'No one in this segment yet',
      segmentBody: 'Contacts appear here as soon as they match its conditions.',
    },
    bulk: {
      label: 'Bulk actions',
      selected_one: '{{count}} selected',
      selected_other: '{{count}} selected',
      addTag: 'Add tag',
      removeTag: 'Remove tag',
      delete: 'Delete',
      clear: 'Clear',
      noTags: 'No tags yet.',
      tagAdded: 'Tag added',
      tagRemoved: 'Tag removed',
      addedBody_one: '{{count}} contact newly tagged.',
      addedBody_other: '{{count}} contacts newly tagged.',
      removedBody_one: 'Removed from {{count}} contact.',
      removedBody_other: 'Removed from {{count}} contacts.',
      tagFailed: 'Could not update the tags',
      deleted_one: '{{count}} contact deleted',
      deleted_other: '{{count}} contacts deleted',
      notDeleted_one: '{{count}} contact was not deleted',
      notDeleted_other: '{{count}} contacts were not deleted',
      confirmTitle_one: 'Delete {{count}} contact?',
      confirmTitle_other: 'Delete {{count}} contacts?',
      confirmBody: 'They are removed from your contact list. This can’t be undone.',
      cancel: 'Cancel',
      confirm_one: 'Delete {{count}} contact',
      confirm_other: 'Delete {{count}} contacts',
      deleting: 'Deleting…',
    },
    form: {
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
      phone: 'Phone',
      username: 'Username',
      status: 'Status',
      notes: 'Notes',
      notesNote: 'visible to the team',
      emailPlaceholder: 'e.g. name@domain.com',
      phonePlaceholder: 'e.g. +216 20 000 000',
      usernamePlaceholder: 'e.g. @username',
      notesPlaceholder: 'e.g. Asked for wholesale prices',
      notSet: 'Not set',
      yes: 'Yes',
      no: 'No',
    },
    create: {
      title: 'New contact',
      cancel: 'Cancel',
      submit: 'Create contact',
      creating: 'Creating…',
      needsOne: 'Enter at least a name, an email, a phone or a username.',
    },
    import: {
      title: 'Import contacts from CSV',
      help: 'The first row must be column headers. Recognized columns: first_name, last_name, username, email, phone, notes, tags (separated by ;) and any custom field key. Unknown columns are ignored, and rows whose email already exists are skipped.',
      choose: 'Choose a .csv file',
      pickFirst: 'Pick a file first.',
      rows: 'Rows',
      imported: 'Imported',
      skipped: 'Skipped',
      row: 'Row',
      problem: 'Problem',
      clean: 'Every row imported cleanly.',
      done: 'Done',
      cancel: 'Cancel',
      submit: 'Import',
      importing: 'Importing…',
    },
    detail: {
      loading: 'Loading contact',
      notFound: 'Contact not found',
      back: 'Contacts',
      added: 'added {{date}}',
      last: {
        ago: 'last interaction {{when}}',
        today: 'last interaction at {{when}}',
        date: 'last interaction on {{when}}',
      },
      noInteraction: 'no interactions yet',
      save: 'Save',
      saving: 'Saving…',
      noChanges: 'No changes',
      unsaved: 'Unsaved changes',
      saved: 'Saved',
      savedToast: 'Contact saved',
      profile: 'Profile',
      customFields: 'Custom fields',
      manageFields: 'Manage fields',
      loadingFields: 'Loading fields',
      noFields: 'No custom fields yet. An admin can add them in',
      noFieldsLink: 'Settings › Custom fields',
      dangerTitle: 'Delete this contact',
      dangerBody: 'Removes {{name}} from the {{workspace}} workspace.',
      dangerBodyPlain: 'Removes {{name}} from this workspace.',
      adminsOnly: 'Only admins can delete a contact.',
      delete: 'Delete contact',
      confirmTitle: 'Delete {{name}}?',
      confirmBody: 'The contact is removed from your list. This can’t be undone.',
      cancel: 'Cancel',
      deleting: 'Deleting…',
      deletedToast: 'Contact deleted',
      tags: 'Tags',
      manageTags: 'Manage tags',
      noTags: 'No tags yet.',
      addTag: 'Add a tag…',
      addTagLabel: 'Add a tag',
    },
  },
  filterPill: {
    filterBy: 'Filter by {{label}}',
    is: '{{label}} is {{value}}',
    remove: 'Remove {{label}} filter',
    panel: '{{label}} filter',
  },
  quick: {
    breadcrumb: 'Breadcrumb',
    kinds: {
      comment: { title: 'Auto-DM links from comments', lede: 'Reply privately to everyone who comments on a post.' },
      dm: { title: 'Respond to your DMs', lede: 'Answer messages with a link, automatically.' },
      story: { title: 'Generate leads with stories', lede: 'Send a link to everyone who replies to your story.' },
    },
    cancel: 'Cancel',
    stepsLabel: 'Steps',
    liveAfter: 'Goes live after step {{count}}',
    liveEdit: 'Changes apply as soon as you publish',
    recap: {
      title: 'What goes live',
      titleEdit: 'What gets published',
      account: 'Account',
      allChannels: 'Every connected channel',
      trigger: 'Trigger',
      commentsOn: 'Comments on “{{caption}}”',
      commentsOnScheduled: 'Comments on “{{caption}}”, scheduled',
      commentsAny: 'Comments on every post',
      chosenPost: 'the chosen post',
      messages: 'Incoming DMs',
      storyOne: 'Replies to the story “{{label}}”',
      storyAny: 'Replies to all your stories',
      words: 'Words',
      anyWord: 'Any',
      quoted: '“{{word}}”',
      sends: 'Sends',
      dmWithLink: 'A DM with the “{{label}}” button to {{host}}',
      dmOnly: 'A DM',
      publicReply: 'a public reply under the comment',
      heart: 'a heart on their message',
      followersOnly: 'the link to followers only',
      consequence: {
        comment: 'Once live, it answers your customers’ real comments.',
        dm: 'Once live, it answers your customers’ real messages.',
        story: 'Once live, it answers real replies to your stories.',
        scheduled: 'It switches on when the post is published, then answers real comments.',
        liveEdit: 'This automation is live: changes reach real customers as soon as you publish.',
      },
    },
    stepOf: 'Step {{n}} of {{total}}',
    stepOfNamed: 'Step {{n}} of {{total}} · {{name}}',
    backTo: 'Back to step {{n}}: {{name}}',
    stepDone: 'done',
    step: {
      post: 'Post',
      comment: 'Comments',
      message: 'Message received',
      story: 'Story',
      reply: 'Replies',
      dm: 'Private message',
      dmReply: 'Your reply',
    },
    nextTo: {
      comment: 'Next: comments →',
      dm: 'Next: private message →',
      dmReply: 'Next: your reply →',
      reply: 'Next: replies →',
    },
    previous: '← Back',
    goLive: 'Go live',
    publishing: 'Going live…',
    publishChanges: 'Publish changes',
    openBuilder: 'Open in the visual editor',
    loading: 'Loading the automation…',
    title: {
      post: 'When someone comments…',
      comment: 'And this comment has…',
      message: 'When someone DMs you…',
      story: 'When someone replies to…',
      reply: 'And this reply has…',
      dmComment: 'They will get…',
      dmDm: 'They’ll get a DM back from you',
      dmStory: 'They’ll get the DM',
    },
    account: 'Account',
    whichPosts: 'Which posts?',
    whichComments: 'Which comments?',
    whichMessages: 'Which messages?',
    whichStories: 'Which stories?',
    whichReplies: 'Which replies?',
    specificPost: 'A specific post or reel',
    anyPost: 'Any post or reel',
    anyPostHint: 'Every post on the account, including ones you publish later.',
    postSource: 'Which posts',
    published: 'Published',
    scheduled: 'Scheduled',
    showAll: 'Show all',
    scheduledHint: 'A scheduled post switches the automation on when it publishes.',
    untitledPost: 'Untitled post',
    loadingPosts: 'Loading posts…',
    noPosts: 'This account has no posts to watch yet.',
    postIdLabel: 'Post or media ID',
    postIdHint: 'This channel cannot list posts — paste the ID from the post’s URL.',
    idPlaceholder: 'e.g. 17900000000000000',
    noImage: 'No image',
    scheduledGrid: {
      loading: 'Loading scheduled posts…',
      error: 'Your scheduled posts could not be loaded.',
      retry: 'Retry',
      none: 'Nothing scheduled on this account yet.',
      schedule: 'Schedule a post',
      goesLive: 'The automation goes live when this post is published.',
    },
    anyStory: 'Any story',
    anyStoryHint: 'Every story on the account, including ones you post later.',
    specificStory: 'A specific story',
    specificStoryHint: 'Only the stories up right now are listed. A story lasts 24 hours; once it is gone nobody can reply to it, so the automation goes quiet.',
    loadingStories: 'Loading stories…',
    noStories: 'No story is up on this account right now. Post one, then pick it here.',
    storyIdLabel: 'Story ID',
    storyIdHint: 'This channel cannot list stories — paste the story’s ID.',
    match: {
      specificWords: 'A specific word or words',
      specificReply: 'Specific words or reactions',
      anyWord: 'Any word',
      anyMessage: 'Any message',
      anyMessageHint: 'Every DM starts it — like a default reply.',
      anyReply: 'Any word or reaction',
    },
    keywords: {
      label: 'Words to look for',
      placeholder: 'e.g. price, link',
      hint: 'Use commas to separate words',
      examples: 'For example:',
      example1: 'Price',
      example2: 'Link',
      example3: 'Shop',
    },
    publicReply: {
      label: 'Reply to their comments under the post',
      hint: 'Posted under the comment, visible to everyone.',
      field: 'Public reply',
    },
    dm: {
      title: 'A DM with a link',
      field: 'DM message',
      placeholder: 'e.g. Thanks for your comment, here is the link',
      left_one: '{{count}} character left',
      left_other: '{{count}} characters left',
      buttonLabel: 'Button label',
      buttonLabelPlaceholder: 'e.g. Open the guide',
      link: 'Link',
      linkPlaceholder: 'e.g. https://example.com',
      removeLink: 'Remove the link',
      addLink: '+ Add a link',
      oneMessage: 'Everything goes in this one message: {{platform}} only lets us write to someone who commented once, until they reply.',
      platform: 'the platform',
    },
    other: 'Other things to automate',
    reactStory: 'React to story replies with a heart',
    reactDm: 'React to their message with a heart',
    reactHint: 'A heart appears on their message, before your reply.',
    followGate: 'Ask to follow before sending the link',
    followGateHint: 'Followers get the link. Everyone else gets this message, and once they follow they write again.',
    askField: 'Message for people who do not follow you',
    channelError: 'Your channels could not be loaded.',
    retry: 'Retry',
    noChannel: 'No channel is connected yet, so this automation would answer on every channel.',
    connect: 'Connect one',
    channelsFailed: 'Your channels could not be loaded, so the automation cannot be scoped to an account. Retry above, then go live.',
    createFailed: 'Could not create the automation.',
    description: 'Built with the “{{title}}” quick automation.',
    toast: {
      live: 'Your automation is live',
      liveBody: 'It answers from now on. You will find it in your automations.',
      set: 'Your automation is set',
      setBody: 'It goes live when the post is published. You will find it in your automations.',
      draft: 'Saved as a draft',
      draftBody_one: '{{count}} issue to fix in the visual editor before it can be published.',
      draftBody_other: '{{count}} issues to fix in the visual editor before it can be published.',
    },
    leave: {
      title: 'Discard this automation?',
      titleChanges: 'Discard your changes?',
      changesLost: 'The automation stays as it is; what you changed here will be lost.',
      draftKept: 'The draft from the earlier attempt stays in your automations; what you changed since will be lost.',
      nothingSaved: 'Nothing has been saved yet. What you set up here will be lost.',
      keep: 'Keep editing',
      discard: 'Discard',
    },
    preview: {
      title: 'Preview',
      tabsLabel: 'View',
      tabs: { post: 'Post', comments: 'Comments', story: 'Story', dm: 'DM' },
      yourAccount: 'your account',
      selectedPost: 'Selected post',
      imageFailed: 'This image could not be loaded',
      noThumbnail: 'This post has no thumbnail',
      pickPost: 'Pick a post to see it here',
      noCaption: 'No caption',
      viewComments: 'View all comments',
      everyPost: 'Watching every post on this account.',
      scheduledPost: 'Scheduled post — the automation goes live when it is published.',
      comments: 'Comments',
      sampleComment: 'Love this!',
      follower: 'a_follower',
      now: 'now',
      noPublicReply: 'No public reply — the contact only gets the DM.',
      addComment: 'Add a comment…',
      reacted: 'Reacted with a heart',
      selectedStory: 'Selected story',
      storyFailed: 'This story could not be loaded',
      storyNoPreview: 'This story has no preview',
      pickStory: 'Pick a story to see it here',
      anyStory: 'Your automation will work for any story',
      sendMessage: 'Send message…',
      hi: 'Hi!',
      repliedToStory: 'Replied to your story',
      story: 'Story',
      writeDm: 'Write the DM to see it here.',
      gateNote: 'Above: what followers get. Not following yet? They get:',
      message: 'Message…',
    },
    problems: {
      words: {
        comment: 'Add at least one word to look for in the comment.',
        message: 'Add at least one word to look for in the message.',
        reply: 'Add at least one word to look for in the reply.',
      },
      tooManyWords: 'Keep to {{max}} words at most.',
      wordTooLong: 'Keep each word under {{max}} characters.',
      publicReplyEmpty: 'Write the public reply, or turn it off.',
      publicReplyLong: 'Keep the public reply under {{max}} characters.',
      askEmpty: 'Write the message for people who do not follow you yet.',
      askEmptyOrOff: 'Write the message for people who do not follow you yet, or turn the follow check off.',
      askLong: 'Keep the message for non-followers under {{max}} characters.',
      post: 'Choose a post to continue.',
      story: 'Choose a story to continue.',
      dmEmpty: 'Write the DM they will receive.',
      dmLong: 'Keep the DM under {{max}} characters.',
      buttonLabel: 'Give the link button a label.',
      buttonLong: 'Keep the button label under {{max}} characters.',
      buttonUrl: 'Enter a full https:// link for the button.',
      linkMissing: 'Add the link this DM sends.',
      storyNeedsInstagram: 'Story replies only exist on Instagram: connect an Instagram account.',
    },
  },
};
